import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import useSWR, { mutate as swrMutate } from "swr";
import api, { money, fmtDate, downloadFile } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DateInputES } from "@/components/ui/DateInputES";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import BookingPlanCell from "@/components/BookingPlanCell";
import GenderIcon from "@/components/GenderIcon";
import { ArrowLeft, TrendingUp, Plus, UserPlus, Search, Check, DollarSign, Pencil, MountainSnow, X, FileDown, Heart, Copy, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { EXP_CATEGORY, BOOKING, ROOM_TYPE, PAY_METHOD, PAY_KIND, ACC_STATUS, TRANSPORT_STATUS } from "@/lib/labels";

const EXP_CATS = ["accommodation","vehicles","fuel","food","flights","guides","permits","activities","marketing","commissions","stripe","insurance","unexpected","other"];

// Colored pill/dot classes for booking status inline dropdown
const STATUS_PILL = {
  lead: "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300",
  interested: "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300",
  deposit_pending: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300",
  deposit_paid: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300",
  confirmed: "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300",
  partial_paid: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300",
  fully_paid: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300",
  cancelled: "bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300",
  refunded: "bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300",
  waitlist: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300",
};
const STATUS_DOT = {
  lead: "bg-sky-500", interested: "bg-sky-500", deposit_pending: "bg-amber-500",
  deposit_paid: "bg-emerald-500", confirmed: "bg-sky-500", partial_paid: "bg-amber-500",
  fully_paid: "bg-emerald-500", cancelled: "bg-red-500", refunded: "bg-red-500",
  waitlist: "bg-slate-400",
};

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function TourDetail() {
  const { id } = useParams();
  const { data: tour, mutate } = useSWR(`/tours/${id}`, fetcher);
  const { data: finance, mutate: mutateFinance } = useSWR(`/tours/${id}/finance`, fetcher);
  const { data: participants, mutate: mutateParticipants } = useSWR(`/participants?tour_id=${id}`, fetcher);
  const { data: allClients } = useSWR("/clients", fetcher);
  const { data: allGuides } = useSWR("/guides", fetcher);
  const { data: expenses, mutate: mutateExpenses } = useSWR(`/expenses?tour_id=${id}`, fetcher);
  const { data: accommodations, mutate: mutateAccommodations } = useSWR(`/accommodations?tour_id=${id}`, fetcher);
  const { data: roomsEstimate, mutate: mutateRoomsEstimate } = useSWR(`/tours/${id}/rooms-estimate`, fetcher);
  const { data: transports, mutate: mutateTransports } = useSWR(`/transports?tour_id=${id}`, fetcher);
  const { data: flights, mutate: mutateFlights } = useSWR(`/flights?tour_id=${id}`, fetcher);
  const { data: itinerary, mutate: mutateItinerary } = useSWR(`/itinerary?tour_id=${id}`, fetcher);

  // Auto-generar días del itinerario en base a start_date/end_date del tour si aún no existen
  const itineraryAutoGenTried = React.useRef(false);
  React.useEffect(() => {
    if (!tour || !itinerary) return;
    if (itineraryAutoGenTried.current) return;
    if ((itinerary?.length || 0) > 0) return;
    if (!tour.start_date || !tour.end_date) return;
    itineraryAutoGenTried.current = true;
    api.post(`/itinerary/auto-generate/${id}`).then(() => mutateItinerary()).catch(() => {});
  }, [tour, itinerary, id, mutateItinerary]);

  // Simulator
  const [simPax, setSimPax] = useState(0);
  const [simPrice, setSimPrice] = useState(0);
  const [simExpenses, setSimExpenses] = useState(0);

  // New-expense dialog
  const emptyExp = { concept: "", category: "other", cost_type: "global",
                     unit_amount: 0, quantity: 1, amount: 0,
                     currency: "USD", exchange_rate: 1,
                     date: new Date().toISOString().slice(0,10) };
  const [expOpen, setExpOpen] = useState(false);

  // Ordenar participantes por parejas (juntas) + solteros al final. Cada pareja recibe un color pastel único.
  const orderedParticipants = React.useMemo(() => {
    if (!participants || participants.length === 0) return [];
    const seen = new Set();
    const pairs = [];
    const solos = [];
    for (const p of participants) {
      if (seen.has(p.id)) continue;
      const pEmail = (p.email || "").toLowerCase();
      const partner = participants.find((op) => {
        if (op.id === p.id) return false;
        const opEmail = (op.email || "").toLowerCase();
        const pPartner = (p.partner_email || "").toLowerCase();
        const opPartner = (op.partner_email || "").toLowerCase();
        return (pPartner && pPartner === opEmail) || (opPartner && opPartner === pEmail);
      });
      if (partner && !seen.has(partner.id)) {
        // Poner primero al que tiene apellido "menor" alfabético para estabilidad
        const [a, b] = [p, partner].sort((x, y) =>
          `${x.last_name || ""} ${x.first_name || ""}`.localeCompare(
            `${y.last_name || ""} ${y.first_name || ""}`
          )
        );
        pairs.push([a, b]);
        seen.add(a.id);
        seen.add(b.id);
      } else {
        solos.push(p);
        seen.add(p.id);
      }
    }
    // Sort pairs by first participant's last_name for stable order
    pairs.sort(([a], [c]) =>
      `${a.last_name || ""} ${a.first_name || ""}`.localeCompare(
        `${c.last_name || ""} ${c.first_name || ""}`
      )
    );
    solos.sort((x, y) =>
      `${x.last_name || ""} ${x.first_name || ""}`.localeCompare(
        `${y.last_name || ""} ${y.first_name || ""}`
      )
    );
    const palette = [
      "bg-pink-50 dark:bg-pink-950/30",
      "bg-amber-50 dark:bg-amber-950/30",
      "bg-emerald-50 dark:bg-emerald-950/30",
      "bg-sky-50 dark:bg-sky-950/30",
      "bg-violet-50 dark:bg-violet-950/30",
      "bg-rose-50 dark:bg-rose-950/30",
      "bg-orange-50 dark:bg-orange-950/30",
      "bg-teal-50 dark:bg-teal-950/30",
      "bg-indigo-50 dark:bg-indigo-950/30",
      "bg-fuchsia-50 dark:bg-fuchsia-950/30",
    ];
    const rows = [];
    pairs.forEach(([a, b], idx) => {
      const bg = palette[idx % palette.length];
      rows.push({ p: a, bg });
      rows.push({ p: b, bg });
    });
    solos.forEach((p) => rows.push({ p, bg: "" }));
    return rows;
  }, [participants]);
  const emptyTransport = {
    company: "", vehicle_type: "Van", plate: "", capacity_pax: 8, capacity_luggage: 8,
    pickup_place: "", pickup_datetime: "", return_place: "", return_datetime: "",
    price: 0, currency: "USD", status: "in_progress", notes: "",
  };
  const [transportOpen, setTransportOpen] = useState(false);
  const [transportForm, setTransportForm] = useState(emptyTransport);
  const [editTransportId, setEditTransportId] = useState(null);
  const [editTransportForm, setEditTransportForm] = useState(emptyTransport);
  const [addDayOpen, setAddDayOpen] = useState(false);
  const [addDayDate, setAddDayDate] = useState("");
  const [addingDay, setAddingDay] = useState(false);
  // Cards expandibles: click en la tarjeta para desplegar detalles
  const [expandedTransports, setExpandedTransports] = useState({});
  // Hoteles expandibles al clic (igual que transportes)
  const [expandedAccs, setExpandedAccs] = useState({});
  const toggleTransport = (tid) => setExpandedTransports((s) => ({ ...s, [tid]: !s[tid] }));
  const toggleAcc = (aid) => setExpandedAccs((s) => ({ ...s, [aid]: !s[aid] }));
  const [expandedFlights, setExpandedFlights] = useState({});
  const toggleFlight = (fid) => setExpandedFlights((s) => ({ ...s, [fid]: !s[fid] }));
  // Vuelos
  const emptyFlightLeg = { airline: "", flight_number: "", date: "", time: "", from_airport: "", to_airport: "", price: 0 };
  const emptyFlight = { airline: "", flight_number: "", date: "", time: "", from_airport: "", to_airport: "", price: 0, has_stopover: false, layovers: [], notes: "" };
  const [flightOpen, setFlightOpen] = useState(false);
  const [flightForm, setFlightForm] = useState(emptyFlight);
  const [editFlightId, setEditFlightId] = useState(null);
  const [savingFlight, setSavingFlight] = useState(false);
  const openNewFlight = () => { setEditFlightId(null); setFlightForm(emptyFlight); setFlightOpen(true); };
  const openEditFlight = (f) => {
    setEditFlightId(f.id);
    setFlightForm({
      airline: f.airline || "", flight_number: f.flight_number || "", date: f.date || "", time: f.time || "",
      from_airport: f.from_airport || "", to_airport: f.to_airport || "", price: f.price || 0,
      has_stopover: !!(f.has_stopover || (f.layovers || []).length),
      layovers: (f.layovers || []).map((l) => ({ ...emptyFlightLeg, ...l })),
      notes: f.notes || "",
    });
    setFlightOpen(true);
  };
  const submitFlight = async () => {
    if (!flightForm.airline?.trim()) return toast.error("Indica la compañía");
    setSavingFlight(true);
    try {
      const payload = {
        tour_id: id,
        airline: flightForm.airline.trim(),
        flight_number: flightForm.flight_number || null,
        date: flightForm.date || null,
        time: flightForm.time || null,
        from_airport: flightForm.from_airport || null,
        to_airport: flightForm.to_airport || null,
        price: Number(flightForm.price) || 0,
        has_stopover: !!flightForm.has_stopover,
        layovers: flightForm.has_stopover
          ? (flightForm.layovers || []).map((l) => ({
              airline: l.airline || null,
              flight_number: l.flight_number || null,
              date: l.date || null,
              time: l.time || null,
              from_airport: l.from_airport || null,
              to_airport: l.to_airport || null,
              price: Number(l.price) || 0,
            }))
          : [],
        notes: flightForm.notes || null,
      };
      if (editFlightId) {
        await api.patch(`/flights/${editFlightId}`, payload);
        toast.success("Vuelo actualizado");
      } else {
        await api.post("/flights", payload);
        toast.success("Vuelo añadido");
      }
      setFlightOpen(false);
      setFlightForm(emptyFlight);
      setEditFlightId(null);
      mutateFlights();
      mutateFinance && mutateFinance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar el vuelo");
    } finally {
      setSavingFlight(false);
    }
  };
  const flightIsPending = (f) => Boolean(f?._mcp_price_pending);
  const flightTotal = (f) => Number(f.price || 0) + (f.layovers || []).reduce((s, l) => s + Number(l.price || 0), 0);
  const flightsGrandTotal = (flights || []).filter((f) => !flightIsPending(f)).reduce((s, f) => s + flightTotal(f), 0);
  const flightsPendingCount = (flights || []).filter(flightIsPending).length;
  const flightsPendingEstimate = (flights || []).filter(flightIsPending).reduce((s, f) => s + flightTotal(f), 0);
  const submitTransport = async () => {
    if (!transportForm.company?.trim()) return toast.error("Indica la empresa");
    try {
      await api.post("/transports", {
        ...transportForm,
        tour_id: id,
        capacity_pax: Number(transportForm.capacity_pax) || 0,
        capacity_luggage: Number(transportForm.capacity_luggage) || 0,
        price: Number(transportForm.price) || 0,
      });
      toast.success("Transporte añadido");
      setTransportForm(emptyTransport);
      setTransportOpen(false);
      mutateTransports();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    }
  };
  const duplicateTransport = async (t) => {
    try {
      const { id: _id, created_at, updated_at, ...rest } = t;
      await api.post("/transports", {
        ...rest,
        tour_id: id,
        company: `${t.company} (copia)`,
      });
      toast.success("Transporte duplicado");
      mutateTransports();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo duplicar");
    }
  };
  const openEditTransport = (t) => {
    setEditTransportId(t.id);
    setEditTransportForm({
      company: t.company || "",
      vehicle_type: t.vehicle_type || "Van",
      plate: t.plate || "",
      capacity_pax: t.capacity_pax ?? 8,
      capacity_luggage: t.capacity_luggage ?? 8,
      pickup_place: t.pickup_place || "",
      pickup_datetime: t.pickup_datetime || "",
      return_place: t.return_place || "",
      return_datetime: t.return_datetime || "",
      price: t.price ?? 0,
      currency: t.currency || "USD",
      status: t.status || "in_progress",
      notes: t.notes || "",
    });
  };
  const saveEditTransport = async () => {
    if (!editTransportId) return;
    if (!editTransportForm.company?.trim()) return toast.error("Indica la empresa");
    try {
      await api.patch(`/transports/${editTransportId}`, {
        ...editTransportForm,
        capacity_pax: Number(editTransportForm.capacity_pax) || 0,
        capacity_luggage: Number(editTransportForm.capacity_luggage) || 0,
        price: Number(editTransportForm.price) || 0,
      });
      toast.success("Transporte actualizado");
      setEditTransportId(null);
      mutateTransports();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar");
    }
  };
  const [expForm, setExpForm] = useState(emptyExp);

  // Guide per-diem (dietas) dialog
  const tourDays = React.useMemo(() => {
    if (!tour?.start_date || !tour?.end_date) return 0;
    const s = new Date(tour.start_date);
    const e = new Date(tour.end_date);
    if (isNaN(s) || isNaN(e)) return 0;
    return Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1);
  }, [tour?.start_date, tour?.end_date]);

  const [pdOpen, setPdOpen] = useState(false);
  const [pdRows, setPdRows] = useState([]); // [{guide_id, guide_name, days, rate, currency, selected}]

  const openPerDiem = () => {
    const rows = (tour?.guides || []).map((gid) => {
      const g = (allGuides || []).find((x) => x.id === gid);
      if (!g) return null;
      return {
        guide_id: g.id,
        guide_name: `${g.first_name} ${g.last_name}`,
        days: tourDays || 1,
        rate: Number(g.day_rate) || 0,
        currency: g.currency || "USD",
        selected: true,
      };
    }).filter(Boolean);
    setPdRows(rows);
    setPdOpen(true);
  };

  const savePerDiem = async () => {
    const selected = pdRows.filter((r) => r.selected && Number(r.rate) > 0 && Number(r.days) > 0);
    if (selected.length === 0) return toast.error("Selecciona al menos un guía con tarifa y días > 0");
    try {
      await Promise.all(selected.map((r) => api.post("/expenses", {
        tour_id: id,
        category: "per_diem",
        concept: `Dietas ${r.guide_name} — ${r.days} día(s) × ${r.rate}`,
        date: tour?.start_date || new Date().toISOString().slice(0, 10),
        amount: Number(r.rate) * Number(r.days),
        currency: r.currency,
        exchange_rate: 1,
      })));
      toast.success(`${selected.length} dieta(s) añadida(s)`);
      setPdOpen(false);
      setPdRows([]);
      mutateExpenses();
      mutateFinance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  // Add-participant dialog (mismos campos que ficha de cliente)
  const emptyPart = {
    first_name: "", last_name: "", email: "", phone: "", country: "",
    gender: "", birthdate: "", language: "es",
    room_type: "double", single_supplement: 0, booking_status: "deposit_pending", price: 0,
    passport_number: "", passport_expiry: "",
    dietary: "", allergies: "", medical: "",
    emergency_contact: "", emergency_contact_phone: "",
    tshirt_size: "M", private_notes: "",
  };
  const [partOpen, setPartOpen] = useState(false);
  const [partForm, setPartForm] = useState(emptyPart);
  const [partQuery, setPartQuery] = useState("");
  const [linkedClientEmail, setLinkedClientEmail] = useState("");

  // Add-payment dialog (per participant)
  const emptyPay = {
    participant_id: "", tour_id: "", amount: 0, currency: "USD", exchange_rate: 1,
    method: "stripe", kind: "deposit", date: new Date().toISOString().slice(0, 10),
    reference: "", notes: "", is_refund: false,
  };
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState(emptyPay);
  const [payParticipant, setPayParticipant] = useState(null);

  // Edit-participant dialog
  const [editPartOpen, setEditPartOpen] = useState(false);
  const [editPartForm, setEditPartForm] = useState({});
  const [editPartId, setEditPartId] = useState(null);
  const [editBookings, setEditBookings] = useState([]); // {participant_id, tour_id, tour_name, tour_start, room_type, price, single_supplement, partner_email, booking_status}
  const [editReservas, setEditReservas] = useState({}); // {participant_id: {room_type, price, single_supplement, partner_email, booking_status}}
  const [activePartTab, setActivePartTab] = useState("__personal__");

  // Accommodation (hotel) dialog — crear/editar desde la ficha del tour
  const emptyAcc = {
    hotel_name: "", city: "", date: "", check_in: "", check_out: "",
    nights: 1, rooms_count: 1, beds_count: 0, price: 0, currency: "USD",
    property_type: "hotel",
    confirmation_status: "in_progress", free_cancellation_until: "",
    room_types: [{ type: "double", count: 1, price: 0 }],
    breakfast_included: true, lunch_included: false, dinner_included: false,
  };
  const [accOpen, setAccOpen] = useState(false);
  const [accForm, setAccForm] = useState(emptyAcc);
  const [accEditId, setAccEditId] = useState(null);

  // Edit/Delete expense dialog
  const [expEditOpen, setExpEditOpen] = useState(false);
  const [expEditForm, setExpEditForm] = useState({});
  const [expEditId, setExpEditId] = useState(null);

  const openEditExp = (e) => {
    setExpEditId(e.id);
    const savedUnit = (e.unit_amount != null && !isNaN(Number(e.unit_amount)))
      ? Number(e.unit_amount)
      : Number(e.amount ?? 0); // legacy: usa el total como precio unitario
    const savedQty = Number(e.quantity) > 0 ? Number(e.quantity) : 1;
    setExpEditForm({
      concept: e.concept || "",
      category: e.category || "other",
      cost_type: e.cost_type || "global",
      amount: e.amount ?? 0,
      unit_amount: savedUnit,
      quantity: savedQty,
      currency: e.currency || "USD",
      exchange_rate: e.exchange_rate ?? 1,
      date: e.date || new Date().toISOString().slice(0, 10),
      notes: e.notes || "",
    });
    setExpEditOpen(true);
  };

  const saveEditExp = async () => {
    if (!expEditId) return;
    if (!expEditForm.concept?.trim()) return toast.error("Falta concepto");
    const unit = Number(expEditForm.unit_amount);
    const qty = Number(expEditForm.quantity) || 1;
    // Si hay precio unitario válido → total = unit * qty; si no, total = amount (compat).
    const hasUnit = Number.isFinite(unit) && unit > 0;
    const total = hasUnit ? +(unit * qty).toFixed(2) : (Number(expEditForm.amount) || 0);
    try {
      await api.patch(`/expenses/${expEditId}`, {
        ...expEditForm,
        unit_amount: hasUnit ? unit : null,
        quantity: qty,
        amount: total,
        exchange_rate: Number(expEditForm.exchange_rate) || 1,
      });
      toast.success("Gasto actualizado");
      setExpEditOpen(false);
      setExpEditId(null);
      mutateExpenses();
      mutateFinance();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const deleteExpense = async (e) => {
    if (!confirm(`¿Eliminar el gasto "${e.concept}"?`)) return;
    try {
      await api.delete(`/expenses/${e.id}`);
      toast.success("Gasto eliminado");
      mutateExpenses();
      mutateFinance();
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  // Quitar participante del tour (con opción de transferir a otro tour)
  const [removePartOpen, setRemovePartOpen] = useState(false);
  const [removePartTarget, setRemovePartTarget] = useState(null);
  const [transferTourId, setTransferTourId] = useState("");
  const [movePayments, setMovePayments] = useState(true);
  const [removeMode, setRemoveMode] = useState("transfer"); // "transfer" | "delete"
  const { data: allTours } = useSWR("/tours", fetcher);

  const openRemovePart = (p) => {
    setRemovePartTarget(p);
    setTransferTourId("");
    setMovePayments(true);
    setRemoveMode("transfer");
    setRemovePartOpen(true);
  };

  const confirmRemovePart = async () => {
    if (!removePartTarget) return;
    if (removeMode === "transfer") {
      if (!transferTourId) return toast.error("Selecciona el tour destino");
      try {
        const { data } = await api.post(`/participants/${removePartTarget.id}/transfer`, {
          target_tour_id: transferTourId,
          move_payments: movePayments,
        });
        toast.success(`Cliente movido${data.payments_moved ? ` con ${data.payments_moved} pago(s)` : ""}`);
        setRemovePartOpen(false);
        setRemovePartTarget(null);
        mutateParticipants();
        mutateFinance();
        mutateRoomsEstimate();
        mutateAccommodations();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al transferir");
      }
      return;
    }
    // Delete flow
    try {
      await api.delete(`/participants/${removePartTarget.id}`);
      toast.success("Participante eliminado del tour");
      setRemovePartOpen(false);
      setRemovePartTarget(null);
      mutateParticipants();
      mutateFinance();
      mutateRoomsEstimate();
      mutateAccommodations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    }
  };

  // Compatibilidad: la función anterior ahora abre el diálogo con opciones
  const removeParticipant = (p) => openRemovePart(p);

  const openNewAcc = () => {
    setAccEditId(null);
    setAccForm(emptyAcc);
    setAccOpen(true);
  };

  const openEditAcc = (a) => {
    setAccEditId(a.id);
    // Reconstruye la lista de room_types desde la ficha existente
    let rts = Array.isArray(a.room_types) && a.room_types.length > 0
      ? a.room_types.map((r) => ({ type: r.type || "double", count: Number(r.count) || 1, price: Number(r.price) || 0 }))
      : [{ type: "double", count: Number(a.rooms_count) || 1, price: Number(a.price) || 0 }];
    setAccForm({
      hotel_name: a.hotel_name || "",
      city: a.city || "",
      date: a.date || "",
      check_in: a.check_in || "",
      check_out: a.check_out || "",
      nights: a.nights ?? 1,
      rooms_count: a.rooms_count ?? 1,
      beds_count: a.beds_count ?? 0,
      price: a.price ?? 0,
      currency: a.currency || "USD",
      property_type: a.property_type || "hotel",
      confirmation_status: a.confirmation_status || "in_progress",
      free_cancellation_until: a.free_cancellation_until || "",
      room_types: rts,
      breakfast_included: a.breakfast_included ?? true,
      lunch_included: a.lunch_included ?? false,
      dinner_included: a.dinner_included ?? false,
    });
    setAccOpen(true);
  };

  const saveAcc = async () => {
    if (!accForm.hotel_name?.trim()) return toast.error("Falta el nombre del hotel");
    const rts = (accForm.room_types || [])
      .filter((r) => Number(r.count) > 0)
      .map((r) => ({ type: r.type, count: Number(r.count) || 0, price: Number(r.price) || 0 }));
    const totalRooms = rts.reduce((s, r) => s + r.count, 0) || 1;
    const totalPrice = rts.reduce((s, r) => s + r.count * r.price, 0);
    // Weighted-average price/room for backward compat
    const avgPrice = totalRooms > 0 ? totalPrice / totalRooms : 0;
    try {
      const payload = {
        ...accForm,
        tour_id: id,
        nights: Number(accForm.nights) || 1,
        rooms_count: totalRooms,
        beds_count: Number(accForm.beds_count) || 0,
        price: Number(avgPrice.toFixed(2)),
        room_types: rts,
      };
      if (accEditId) {
        await api.patch(`/accommodations/${accEditId}`, payload);
        toast.success("Hotel actualizado");
      } else {
        await api.post("/accommodations", payload);
        toast.success("Hotel añadido");
      }
      setAccOpen(false);
      setAccEditId(null);
      setAccForm(emptyAcc);
      mutateAccommodations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const deleteAcc = async (a) => {
    if (!confirm(`¿Eliminar el hotel "${a.hotel_name}"?`)) return;
    try {
      await api.delete(`/accommodations/${a.id}`);
      toast.success("Hotel eliminado");
      mutateAccommodations();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const openEditPart = async (p) => {
    setEditPartId(p.id);
    setActivePartTab("__personal__");
    setEditPartForm({
      first_name: p.first_name || "",
      last_name: p.last_name || "",
      email: p.email || "",
      phone: p.phone || "",
      country: p.country || "",
      gender: p.gender || "",
      birthdate: p.birthdate || "",
      passport_number: p.passport_number || "",
      passport_expiry: p.passport_expiry || "",
      dietary: p.dietary || "",
      allergies: p.allergies || "",
      medical: p.medical || "",
      emergency_contact: p.emergency_contact || "",
      emergency_contact_phone: p.emergency_contact_phone || "",
    });
    // Pre-load current tour's reserva
    const currentTourPrice = tour?.price_per_participant || 0;
    const seedPrice = (p.price && Number(p.price) > 0) ? p.price : currentTourPrice;
    const seedReserva = {
      [p.id]: {
        room_type: p.room_type || "double",
        price: seedPrice,
        single_supplement: p.single_supplement || 0,
        partner_email: p.partner_email || "",
        booking_status: p.booking_status || "lead",
      },
    };
    setEditReservas(seedReserva);
    setEditPartOpen(true);
    // Fetch all bookings of this client (all tours they are enrolled in)
    if (p.email) {
      try {
        const { data } = await api.get(`/clients/by-email/${encodeURIComponent(p.email)}`);
        const bks = data?.bookings || [];
        setEditBookings(bks);
        const reservasMap = { ...seedReserva };
        for (const b of bks) {
          if (!reservasMap[b.participant_id]) {
            const effPrice = (b.price && Number(b.price) > 0) ? b.price : (b.tour_price || 0);
            reservasMap[b.participant_id] = {
              room_type: b.room_type || "double",
              price: effPrice,
              single_supplement: b.single_supplement || 0,
              partner_email: b.partner_email || "",
              booking_status: b.booking_status || "lead",
            };
          }
        }
        setEditReservas(reservasMap);
      } catch {
        setEditBookings([{ participant_id: p.id, tour_id: id, tour_name: tour?.name || "Tour actual", tour_start: tour?.start_date }]);
      }
    }
  };

  const saveEditPart = async () => {
    if (!editPartId) return;
    if (!editPartForm.first_name?.trim() || !editPartForm.last_name?.trim()) {
      return toast.error("Nombre y apellidos son obligatorios");
    }
    try {
      if (activePartTab === "__personal__") {
        // Save personal fields once — patch client by email so it propagates to all bookings + client doc
        const em = editPartForm.email;
        const personal = cleanForApi(editPartForm);
        delete personal.email;
        if (em) {
          await api.patch(`/clients/by-email/${encodeURIComponent(em)}?participant_id=${encodeURIComponent(editPartId)}`, personal);
        } else {
          await api.patch(`/participants/${editPartId}`, personal);
        }
        toast.success("Datos personales actualizados");
      } else {
        // Save reserva only to that specific participant
        const r = editReservas[activePartTab] || {};
        const payload = cleanForApi({
          room_type: r.room_type,
          price: Number(r.price) || 0,
          single_supplement: Number(r.single_supplement) || 0,
          partner_email: r.partner_email,
          booking_status: r.booking_status,
        });
        await api.patch(`/participants/${activePartTab}`, payload);
        toast.success("Reserva actualizada");
        swrMutate(`/bookings/${activePartTab}/plan`);
        // Update local reservas map so if the user reopens the tab it still shows saved values
        setEditReservas((prev) => ({ ...prev, [activePartTab]: { ...prev[activePartTab], ...payload } }));
      }
      mutateParticipants();
      mutateFinance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const openPayFor = async (p) => {
    setPayParticipant(p);
    // Try to prefill amount with next due from plan
    try {
      const { data: plan } = await api.get(`/bookings/${p.id}/plan`);
      const suggestedAmount = plan?.next_due?.amount ?? plan?.balance ?? p.price ?? 0;
      const suggestedKind = plan?.next_due?.kind === "deposit" ? "deposit" : "final";
      setPayForm({
        ...emptyPay,
        participant_id: p.id,
        tour_id: id,
        amount: Number(suggestedAmount) || 0,
        currency: tour?.currency || "USD",
        kind: suggestedKind,
      });
    } catch {
      setPayForm({ ...emptyPay, participant_id: p.id, tour_id: id, amount: Number(p.price) || 0 });
    }
    setPayOpen(true);
  };

  const savePayment = async () => {
    if (!payForm.participant_id || !payForm.tour_id) return toast.error("Falta participante o tour");
    if (!(Number(payForm.amount) > 0)) return toast.error("El importe debe ser mayor que 0");
    try {
      await api.post("/payments", {
        ...payForm,
        amount: Number(payForm.amount),
        exchange_rate: Number(payForm.exchange_rate) || 1,
      });
      toast.success("Pago registrado");
      setPayOpen(false);
      setPayParticipant(null);
      setPayForm(emptyPay);
      mutateFinance();
      mutateParticipants();
      swrMutate(`/bookings/${payForm.participant_id}/plan`);
      swrMutate("/payments");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar pago");
    }
  };

  // Filter clients: exclude those already booked on this tour
  const bookedEmails = new Set((participants || []).map((p) => (p.email || "").toLowerCase()));
  const clientOptions = (allClients || [])
    .filter((c) => !bookedEmails.has(c.email))
    .filter((c) => {
      const s = `${c.first_name} ${c.last_name} ${c.email} ${c.country || ""}`.toLowerCase();
      return partQuery === "" || s.includes(partQuery.toLowerCase());
    })
    .slice(0, 50);

  const pickClient = (c) => {
    setLinkedClientEmail(c.email);
    setPartForm((f) => ({
      ...f,
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      email: c.email || "",
      phone: c.phone || "",
      country: c.country || "",
      passport_number: c.passport_number || "",
      passport_expiry: c.passport_expiry || "",
      dietary: c.dietary || "",
      allergies: c.allergies || "",
      medical: c.medical || "",
      tshirt_size: c.tshirt_size || "M",
      price: tour?.price_per_participant ?? f.price,
    }));
  };

  const clearLinked = () => { setLinkedClientEmail(""); setPartForm(emptyPart); };

  // Limpia valores vacíos que Pydantic Literal rechazaría (p. ej. gender: "")
  const cleanForApi = (obj) => {
    const out = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
      // Campos con Literal en backend que no aceptan ""
      if (["gender"].includes(k) && (v === "" || v == null)) return;
      out[k] = v;
    });
    return out;
  };

  const savePart = async () => {
    if (!partForm.first_name?.trim() || !partForm.last_name?.trim() || !partForm.email?.trim()) {
      return toast.error("Nombre, apellidos y email son obligatorios");
    }
    try {
      await api.post("/participants", cleanForApi({ ...partForm, tour_id: id, price: Number(partForm.price) }));
      toast.success(linkedClientEmail ? "Cliente añadido al tour" : "Nueva reserva creada");
      setPartForm(emptyPart); setLinkedClientEmail(""); setPartQuery(""); setPartOpen(false);
      mutateParticipants(); mutateFinance();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const saveExpense = async () => {
    if (!expForm.concept?.trim()) return toast.error("Falta el concepto");
    const unit = Number(expForm.unit_amount) || 0;
    const qty = Number(expForm.quantity) || 1;
    const total = +(unit * qty).toFixed(2);
    try {
      await api.post("/expenses", {
        ...expForm, tour_id: id,
        unit_amount: unit, quantity: qty, amount: total,
        exchange_rate: Number(expForm.exchange_rate) || 1,
      });
      toast.success("Gasto añadido");
      setExpForm(emptyExp); setExpOpen(false);
      mutateExpenses(); mutateFinance();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  React.useEffect(() => {
    if (finance && tour) {
      setSimPax(finance.participants_confirmed || 0);
      setSimPrice(tour.price_per_participant || 0);
      setSimExpenses(finance.budgeted_expenses || 0);
    }
  }, [finance, tour]);

  // Recalcula automáticamente los gastos simulados cuando cambia el pax:
  // gastos = globales + (per_person_unit * pax)
  React.useEffect(() => {
    if (finance) {
      const g = Number(finance.global_expenses || 0);
      const pp = Number(finance.per_person_expenses_unit || 0);
      setSimExpenses(g + pp * Number(simPax || 0));
    }
  }, [simPax, finance]);

  if (!tour || !finance) return <div className="text-slate-500">Cargando…</div>;

  const simRevenue = Number(simPax) * Number(simPrice);
  const simProfit = simRevenue - Number(simExpenses);

  return (
    <div className="space-y-6">
      <Link to="/tours" className="text-sm text-slate-500 hover:text-[#026FCB] inline-flex items-center gap-1">
        <ArrowLeft size={14} />Todas las expediciones
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{tour.name}</h1>
            <StatusBadge value={tour.status} />
          </div>
          <p className="text-slate-500 mt-1">{tour.destination}, {tour.country} · {fmtDate(tour.start_date)} → {fmtDate(tour.end_date)}</p>
          {/* Guides chips */}
          {(tour.guides?.length || 0) > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="tour-guides-chips">
              <span className="text-[10px] mono uppercase tracking-wider text-slate-500">Guías:</span>
              {(tour.guides || []).map((gid) => {
                const g = (allGuides || []).find((x) => x.id === gid);
                if (!g) return null;
                return (
                  <span
                    key={gid}
                    data-testid={`tour-guide-chip-${gid}`}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#026FCB]/10 text-[#026FCB] text-xs border border-[#026FCB]/20"
                  >
                    <MountainSnow size={12} />
                    {g.first_name} {g.last_name}
                    <button
                      className="ml-1 opacity-60 hover:opacity-100"
                      title="Quitar guía"
                      data-testid={`remove-guide-${gid}`}
                      onClick={async () => {
                        const next = (tour.guides || []).filter((x) => x !== gid);
                        try {
                          await api.patch(`/tours/${id}`, { guides: next });
                          toast.success("Guía retirado");
                          mutate();
                        } catch { toast.error("Error"); }
                      }}
                    >
                      <X size={11} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest mono text-slate-500">Beneficio previsto</div>
          <div className={`text-2xl font-bold ${finance.gross_margin_expected >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {money(finance.gross_margin_expected)}
          </div>
          {(() => {
            const n = finance.clients_signed_up || 0;
            const profit = finance.estimated_profit || 0;
            return (
              <>
                <div className="mt-2" data-testid="tour-cost-per-client">
                  <div className="text-[10px] uppercase tracking-widest mono text-slate-500">Coste por cliente</div>
                  <div className="text-lg font-semibold text-amber-600" data-testid="tour-cost-per-client-value">
                    {money(finance.cost_per_client_real || 0)}
                  </div>
                  <div className="text-[10px] text-slate-400 mono">
                    {money(finance.adjusted_expenses || 0)} ÷ {n} {n === 1 ? "cliente" : "clientes"} · hoteles solo hab. necesarias
                  </div>
                </div>
                <div className="mt-2" data-testid="tour-estimated-profit">
                  <div className="text-[10px] uppercase tracking-widest mono text-slate-500">Ganancia estimada</div>
                  <div className={`text-lg font-semibold ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="tour-estimated-profit-value">
                    {money(profit)}
                  </div>
                  <div className="text-[10px] text-slate-400 mono">
                    {money(finance.revenue_signed_up || 0)} ingresos − {money(finance.adjusted_expenses || 0)} gastos
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-4">
          <div className="text-xs mono uppercase text-slate-500">Ingresos previstos</div>
          <div className="text-xl font-bold mt-1">{money(finance.expected_revenue)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs mono uppercase text-slate-500">Cobrado</div>
          <div className="text-xl font-bold mt-1 text-emerald-600">{money(finance.net_collected)}</div>
          <div className="text-xs text-slate-500 mt-1">Pendiente: {money(finance.pending_from_clients)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs mono uppercase text-slate-500">Gastos estimados</div>
          <div className="text-xl font-bold mt-1 text-amber-600">{money(finance.budgeted_expenses)}</div>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="finance">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="finance" data-testid="tab-finance">Rentabilidad</TabsTrigger>
          <TabsTrigger value="participants" data-testid="tab-participants">
            Participantes ({(participants?.length || 0) + (tour.guides?.length || 0)})
          </TabsTrigger>
          <TabsTrigger value="accommodations" data-testid="tab-accommodations">Hoteles ({accommodations?.length || 0})</TabsTrigger>
          <TabsTrigger value="transport">Transporte ({transports?.length || 0})</TabsTrigger>
          <TabsTrigger value="flights" data-testid="tab-flights">Vuelos ({flights?.length || 0})</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Gastos ({expenses?.length || 0})</TabsTrigger>
          <TabsTrigger value="itinerary">Itinerario ({itinerary?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="finance" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp size={18} />Simulador de rentabilidad</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-4 gap-4">
              <div><Label>Nº participantes</Label><Input data-testid="sim-pax" type="number" value={simPax} onChange={(e) => setSimPax(e.target.value)} /></div>
              <div><Label>Precio por participante</Label><Input data-testid="sim-price" type="number" value={simPrice} onChange={(e) => setSimPrice(e.target.value)} /></div>
              <div>
                <Label>Gastos totales</Label>
                <Input data-testid="sim-expenses" type="number" value={simExpenses} onChange={(e) => setSimExpenses(e.target.value)} />
                {finance && Number(finance.per_person_expenses_unit) > 0 && (
                  <div className="text-[10px] mono uppercase tracking-wider text-slate-500 mt-1">
                    {money(finance.global_expenses)} fijo + {money(finance.per_person_expenses_unit)}×pax
                  </div>
                )}
              </div>
              <div className="flex flex-col justify-end">
                <div className="text-xs mono uppercase text-slate-500">Beneficio simulado</div>
                <div data-testid="sim-profit" className={`text-2xl font-bold ${simProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(simProfit)}</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="participants" className="mt-4 space-y-3">
          {/* Resumen de personas totales */}
          <div className="flex items-center justify-between flex-wrap gap-3 rounded-lg border border-slate-200 dark:border-[#25334D] bg-slate-50 dark:bg-[#1A2336]/40 px-4 py-3">
            <div className="flex items-center gap-6 flex-wrap text-sm" data-testid="tour-people-summary">
              <div>
                <div className="text-[10px] mono uppercase tracking-wider text-slate-500">Clientes</div>
                <div className="text-xl font-bold text-[#026FCB]">{participants?.length || 0}</div>
              </div>
              <div className="text-slate-300 dark:text-[#25334D] text-2xl font-thin">+</div>
              <div>
                <div className="text-[10px] mono uppercase tracking-wider text-slate-500">Guías</div>
                <div className="text-xl font-bold text-emerald-600">{tour.guides?.length || 0}</div>
              </div>
              <div className="text-slate-300 dark:text-[#25334D] text-2xl font-thin">=</div>
              <div>
                <div className="text-[10px] mono uppercase tracking-wider text-slate-500">Total personas</div>
                <div className="text-2xl font-bold">{(participants?.length || 0) + (tour.guides?.length || 0)}</div>
              </div>
            </div>
            <Dialog open={partOpen} onOpenChange={(o) => {
              setPartOpen(o);
              if (o) { setPartForm({ ...emptyPart, price: tour?.price_per_participant || 0 }); }
              else { setPartForm(emptyPart); setLinkedClientEmail(""); setPartQuery(""); }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="tour-add-participant-btn" className="bg-[#026FCB] hover:bg-[#0358a5]"><UserPlus size={16} className="mr-2" />Añadir participante</Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Añadir participante a {tour?.name}</DialogTitle>
                  <p className="text-sm text-slate-500 mt-1">Elige un cliente ya registrado o crea uno nuevo desde cero.</p>
                </DialogHeader>

                <div className="space-y-5">
                  <div>
                    <h3 className="text-xs mono uppercase tracking-widest text-[#026FCB] mb-2">Cliente existente</h3>
                    {linkedClientEmail ? (
                      <div className="flex items-center justify-between border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Check size={16} className="text-emerald-600" />
                          <span className="font-medium">{partForm.first_name} {partForm.last_name}</span>
                          <span className="text-slate-500">— {partForm.email}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={clearLinked} data-testid="clear-linked-client">Cambiar</Button>
                      </div>
                    ) : (
                      <>
                        <div className="relative mb-2">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                          <Input data-testid="tour-client-search" className="pl-9" placeholder="Buscar por nombre o email…" value={partQuery} onChange={(e) => setPartQuery(e.target.value)} />
                        </div>
                        <div className="max-h-40 overflow-y-auto border border-slate-200 dark:border-[#25334D] rounded-lg divide-y divide-slate-100 dark:divide-[#25334D]/40">
                          {clientOptions.length === 0 && (
                            <div className="px-3 py-4 text-sm text-slate-500">{partQuery ? "Sin coincidencias." : "No hay clientes libres para este tour."}</div>
                          )}
                          {clientOptions.map((c) => (
                            <button
                              key={c.email}
                              type="button"
                              data-testid={`tour-pick-client-${c.email}`}
                              onClick={() => pickClient(c)}
                              className="w-full text-left px-3 py-2 hover:bg-sky-50 dark:hover:bg-[#1A2336]/60 flex items-center justify-between"
                            >
                              <div>
                                <div className="text-sm font-medium">{c.first_name} {c.last_name}</div>
                                <div className="text-xs text-slate-500">{c.email} · {c.country || "—"}</div>
                              </div>
                              <span className="text-xs mono text-[#026FCB]">{c.tours_count} tour(s)</span>
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-slate-500 mt-2">O rellena los datos abajo para crear un cliente nuevo directamente.</p>
                      </>
                    )}
                  </div>

                  <div>
                    <h3 className="text-xs mono uppercase tracking-widest text-[#026FCB] mb-2">Datos del participante</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Nombre *</Label><Input data-testid="tour-part-first" value={partForm.first_name} onChange={(e) => setPartForm({...partForm, first_name: e.target.value})} /></div>
                      <div><Label>Apellidos *</Label><Input data-testid="tour-part-last" value={partForm.last_name} onChange={(e) => setPartForm({...partForm, last_name: e.target.value})} /></div>
                      <div><Label>Email *</Label><Input data-testid="tour-part-email" type="email" value={partForm.email} onChange={(e) => setPartForm({...partForm, email: e.target.value})} /></div>
                      <div><Label>Teléfono</Label><Input value={partForm.phone} onChange={(e) => setPartForm({...partForm, phone: e.target.value})} /></div>
                      <div><Label>País</Label><Input value={partForm.country} onChange={(e) => setPartForm({...partForm, country: e.target.value})} /></div>
                      <div><Label>Género</Label>
                        <Select value={partForm.gender || "__none__"} onValueChange={(v) => setPartForm({...partForm, gender: v === "__none__" ? "" : v})}>
                          <SelectTrigger data-testid="tour-part-gender"><SelectValue placeholder="Sin especificar" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Sin especificar —</SelectItem>
                            <SelectItem value="male">Hombre</SelectItem>
                            <SelectItem value="female">Mujer</SelectItem>
                            <SelectItem value="other">Otro</SelectItem>
                            <SelectItem value="prefer_not_to_say">Prefiere no decirlo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Fecha de nacimiento</Label>
                        <DateInputES data-testid="tour-part-birthdate" value={partForm.birthdate} onChange={(e) => setPartForm({...partForm, birthdate: e.target.value})} />
                      </div>
                      <div><Label>Pasaporte</Label><Input value={partForm.passport_number} onChange={(e) => setPartForm({...partForm, passport_number: e.target.value})} /></div>
                      <div><Label>Caduca</Label><DateInputES value={partForm.passport_expiry} onChange={(e) => setPartForm({...partForm, passport_expiry: e.target.value})} /></div>
                      <div><Label>Persona de contacto</Label><Input placeholder="Nombre y parentesco" value={partForm.emergency_contact} onChange={(e) => setPartForm({...partForm, emergency_contact: e.target.value})} /></div>
                      <div><Label>Teléfono de contacto</Label><Input placeholder="+34 600 000 000" value={partForm.emergency_contact_phone} onChange={(e) => setPartForm({...partForm, emergency_contact_phone: e.target.value})} /></div>
                      <div className="col-span-2"><Label>Dieta / alergias / condiciones médicas</Label>
                        <Input placeholder="Dieta" className="mb-2" value={partForm.dietary} onChange={(e) => setPartForm({...partForm, dietary: e.target.value})} />
                        <Input placeholder="Alergias" className="mb-2" value={partForm.allergies} onChange={(e) => setPartForm({...partForm, allergies: e.target.value})} />
                        <Input placeholder="Condiciones médicas" value={partForm.medical} onChange={(e) => setPartForm({...partForm, medical: e.target.value})} />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs mono uppercase tracking-widest text-[#026FCB] mb-2">Reserva</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label>Habitación</Label>
                        <Select value={partForm.room_type} onValueChange={(v) => setPartForm({...partForm, room_type: v})}>
                          <SelectTrigger data-testid="tour-part-room"><SelectValue /></SelectTrigger>
                          <SelectContent>{Object.keys(ROOM_TYPE).map((r) => <SelectItem key={r} value={r}>{ROOM_TYPE[r]}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label>Precio pactado</Label>
                        <Input type="number" data-testid="tour-part-price" value={partForm.price} onChange={(e) => setPartForm({...partForm, price: e.target.value})} />
                        <p className="text-[10px] text-slate-500 mt-1">Por defecto {money(tour?.price_per_participant || 0)} (precio del tour). Puedes editarlo.</p>
                      </div>
                      <div><Label>Estado</Label>
                        <Select value={partForm.booking_status} onValueChange={(v) => setPartForm({...partForm, booking_status: v})}>
                          <SelectTrigger data-testid="tour-part-status"><SelectValue /></SelectTrigger>
                          <SelectContent>{["lead","interested","deposit_pending","deposit_paid","confirmed","partial_paid","fully_paid","waitlist"].map((s) => <SelectItem key={s} value={s}>{BOOKING[s]}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {partForm.room_type === "single" && (
                        <div className="col-span-3"><Label>Suplemento habitación simple</Label>
                          <Input type="number" data-testid="tour-part-single-supp" placeholder="Extra que paga por dormir solo" value={partForm.single_supplement || 0} onChange={(e) => setPartForm({...partForm, single_supplement: e.target.value})} />
                          <p className="text-[10px] text-slate-500 mt-1">Se suma al precio pactado al calcular ingresos y saldo.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setPartOpen(false)}>Cancelar</Button>
                  <Button data-testid="tour-part-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={savePart}>Añadir al tour</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="overflow-hidden"><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead className="bg-slate-100 dark:bg-[#1A2336]">
                <tr>
                  <th className="text-left px-3 py-3">Nombre</th>
                  <th className="text-left px-3 py-3">Email</th>
                  <th className="text-left px-3 py-3">Estado</th>
                  <th className="text-left px-3 py-3">Hab.</th>
                  <th className="text-left px-3 py-3">Pareja</th>
                  <th className="text-left px-3 py-3">Plan de pagos</th>
                  <th className="text-right px-3 py-3">Precio</th>
                  <th className="text-right px-3 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {orderedParticipants.map(({ p, bg }) => {
                  // Look up the partner reciprocally: either p.partner_email points to op,
                  // or op.partner_email points back to p.
                  const pEmail = (p.email || "").toLowerCase();
                  const partnerP = (participants || []).find((op) => {
                    if (op.id === p.id) return false;
                    const opEmail = (op.email || "").toLowerCase();
                    const pPartner = (p.partner_email || "").toLowerCase();
                    const opPartner = (op.partner_email || "").toLowerCase();
                    return (pPartner && pPartner === opEmail) || (opPartner && opPartner === pEmail);
                  });
                  const setPartner = async (email) => {
                    try {
                      await api.patch(`/participants/${p.id}`, { partner_email: email || null });
                      await mutateParticipants();
                      await mutateRoomsEstimate();
                    } catch { toast.error("No se pudo guardar la pareja"); }
                  };
                  // Value shown in the select: prefer own field, fallback to reciprocal partner's email
                  const selectedPartnerEmail = p.partner_email || (partnerP ? partnerP.email : "");
                  return (
                  <tr key={p.id} className={`border-t border-slate-100 dark:border-[#25334D]/50 align-top ${bg}`}>
                    <td className="px-2 py-3">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <GenderIcon gender={p.gender} size={12} />
                        <span className="truncate">{p.first_name} {p.last_name}</span>
                      </div>
                    </td>
                    <td className="px-2 py-3 text-slate-500 truncate" title={p.email}>{p.email}</td>
                    <td className="px-2 py-3">
                      <Select
                        value={p.booking_status || "lead"}
                        onValueChange={async (v) => {
                          try {
                            await api.patch(`/participants/${p.id}`, { booking_status: v });
                            toast.success("Estado actualizado");
                            mutateParticipants();
                            mutateFinance();
                            swrMutate(`/bookings/${p.id}/plan`);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Error al actualizar");
                          }
                        }}
                      >
                        <SelectTrigger className={`h-8 text-xs w-[170px] font-medium border ${STATUS_PILL[p.booking_status || "lead"] || STATUS_PILL.lead}`} data-testid={`part-status-${p.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["lead","interested","deposit_pending","deposit_paid","confirmed","partial_paid","fully_paid","cancelled","refunded","waitlist"].map((s) => (
                            <SelectItem key={s} value={s}>
                              <span className="inline-flex items-center gap-2">
                                <span className={`inline-block w-2 h-2 rounded-full ${STATUS_DOT[s] || "bg-slate-400"}`} />
                                {BOOKING[s]}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-3">
                      <div className="flex flex-col gap-1">
                        <Select
                          value={p.room_type || "double"}
                          onValueChange={async (v) => {
                            try {
                              const patch = { room_type: v };
                              if (v !== "single") patch.single_supplement = 0;
                              await api.patch(`/participants/${p.id}`, patch);
                              mutateParticipants();
                              mutateRoomsEstimate();
                              mutateFinance();
                            } catch { toast.error("No se pudo actualizar"); }
                          }}
                        >
                          <SelectTrigger data-testid={`room-type-${p.id}`} className="h-7 min-w-[80px] text-[10px] mono uppercase tracking-wider">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.keys(ROOM_TYPE).map((r) => (
                              <SelectItem key={r} value={r} className="text-xs">{ROOM_TYPE[r]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {p.room_type === "single" && (
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="+ Suplemento"
                            data-testid={`single-supp-${p.id}`}
                            className="h-7 text-[11px] mono w-full"
                            defaultValue={p.single_supplement || 0}
                            onBlur={async (e) => {
                              const v = Number(e.target.value) || 0;
                              if (v === (p.single_supplement || 0)) return;
                              try {
                                await api.patch(`/participants/${p.id}`, { single_supplement: v });
                                toast.success("Suplemento actualizado");
                                mutateParticipants();
                                mutateFinance();
                              } catch { toast.error("No se pudo guardar el suplemento"); }
                            }}
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-3">
                      <Select
                        value={selectedPartnerEmail || "__none__"}
                        onValueChange={(v) => setPartner(v === "__none__" ? "" : v)}
                      >
                        <SelectTrigger data-testid={`partner-select-${p.id}`} className="h-8 w-full text-xs">
                          <div className="flex items-center gap-1 truncate">
                            {partnerP ? (
                              <><Heart size={11} className="text-pink-500 fill-pink-500 shrink-0" />
                              <span className="truncate">{partnerP.first_name}</span></>
                            ) : (
                              <span className="text-slate-400">— Sin pareja —</span>
                            )}
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sin pareja —</SelectItem>
                          {(participants || [])
                            .filter((op) => op.id !== p.id && op.email)
                            .map((op) => (
                              <SelectItem key={op.id} value={op.email}>
                                {op.first_name} {op.last_name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-3"><BookingPlanCell participantId={p.id} /></td>
                    <td className="px-2 py-3 text-right mono">
                      {money(((Number(p.price) || tour?.price_per_participant || 0)) + (p.room_type === "single" ? (Number(p.single_supplement) || 0) : 0))}
                      {p.room_type === "single" && (Number(p.single_supplement) || 0) > 0 && (
                        <div className="text-[9px] text-slate-400 mono normal-case">
                          {money(Number(p.price) || tour?.price_per_participant || 0)} + {money(p.single_supplement)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-part-${p.id}`}
                          onClick={() => openEditPart(p)}
                          title="Editar participante"
                          className="h-8 w-8 p-0"
                        >
                          <Pencil size={13} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`add-pay-${p.id}`}
                          onClick={() => openPayFor(p)}
                          title="Añadir pago"
                          className="h-8 w-8 p-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
                        >
                          <DollarSign size={13} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                          data-testid={`remove-part-${p.id}`}
                          onClick={() => removeParticipant(p)}
                          title="Quitar del tour"
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {(!participants || participants.length === 0) && (
                  <tr><td colSpan={8} className="text-center py-10 text-slate-500">Aún sin participantes. Añade el primero.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent></Card>

          {/* Guías asignados al tour */}
          {(tour.guides?.length || 0) > 0 && (
            <div data-testid="tour-guides-section">
              <div className="flex items-center gap-2 mb-2 mt-4">
                <MountainSnow size={14} className="text-emerald-600" />
                <span className="text-xs mono uppercase tracking-widest text-slate-600 dark:text-slate-300">
                  Guías del tour ({tour.guides.length})
                </span>
              </div>
              <Card className="overflow-hidden"><CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm min-w-[860px]">
                  <thead className="bg-emerald-50 dark:bg-emerald-950/30">
                    <tr>
                      <th className="text-left px-3 py-3">Guía</th>
                      <th className="text-left px-3 py-3">Email</th>
                      <th className="text-left px-3 py-3">Teléfono</th>
                      <th className="text-left px-3 py-3">Idiomas</th>
                      <th className="text-left px-3 py-3">Especialidades</th>
                      <th className="text-left px-3 py-3">Habitación</th>
                      <th className="text-right px-3 py-3">Tarifa día</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(tour.guides || []).map((gid) => {
                      const g = (allGuides || []).find((x) => x.id === gid);
                      if (!g) {
                        return (
                          <tr key={gid} className="border-t border-slate-100 dark:border-[#25334D]/50">
                            <td colSpan={7} className="px-2 py-3 text-xs text-slate-400 italic">Guía no encontrado ({gid.slice(0, 8)}…)</td>
                          </tr>
                        );
                      }
                      const currentRT = (tour.guide_room_overrides?.[gid]) || g.room_type || "double";
                      const setGuideRoom = async (rt) => {
                        if (currentRT === rt) return;
                        try {
                          const next = { ...(tour.guide_room_overrides || {}), [gid]: rt };
                          await api.patch(`/tours/${id}`, { guide_room_overrides: next });
                          await mutate();
                          await mutateRoomsEstimate();
                        } catch { toast.error("No se pudo actualizar"); }
                      };
                      return (
                        <tr key={gid} data-testid={`guide-row-${gid}`} className="border-t border-slate-100 dark:border-[#25334D]/50 align-top">
                          <td className="px-2 py-3">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center shrink-0">
                                <MountainSnow size={11} className="text-emerald-600 dark:text-emerald-400" />
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium truncate">{g.first_name} {g.last_name}</div>
                                {g.country && <div className="text-[9px] mono uppercase text-slate-400 truncate">{g.country}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-3 text-slate-500 truncate" title={g.email}>{g.email}</td>
                          <td className="px-2 py-3 text-slate-500 mono text-xs truncate">{g.phone || "—"}</td>
                          <td className="px-2 py-3 text-slate-500 text-xs truncate" title={g.languages || ""}>{g.languages || "—"}</td>
                          <td className="px-2 py-3 text-slate-500 text-xs truncate" title={g.specialties || ""}>{g.specialties || "—"}</td>
                          <td className="px-2 py-3">
                            <div className="inline-flex rounded-md overflow-hidden border border-slate-200 dark:border-[#25334D] text-[9px] mono uppercase tracking-wider">
                              <button
                                type="button"
                                data-testid={`guide-room-${gid}-double`}
                                onClick={() => setGuideRoom("double")}
                                className={`px-1.5 py-1 transition-colors ${
                                  currentRT === "double"
                                    ? "bg-sky-500 text-white"
                                    : "bg-transparent text-slate-500 hover:bg-slate-100 dark:hover:bg-[#1A2336]"
                                }`}
                              >Doble</button>
                              <button
                                type="button"
                                data-testid={`guide-room-${gid}-single`}
                                onClick={() => setGuideRoom("single")}
                                className={`px-1.5 py-1 transition-colors ${
                                  currentRT === "single"
                                    ? "bg-purple-500 text-white"
                                    : "bg-transparent text-slate-500 hover:bg-slate-100 dark:hover:bg-[#1A2336]"
                                }`}
                              >Simple</button>
                            </div>
                          </td>
                          <td className="px-2 py-3 text-right mono text-xs">{money(g.day_rate || 0, g.currency)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent></Card>
              <p className="text-xs text-slate-500 mt-2">
                Para añadir o quitar guías, edita el tour desde <b>Expediciones</b> o usa los chips de la cabecera.
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="accommodations" className="mt-4 space-y-3">
          {/* Estimación de habitaciones necesarias */}
          {roomsEstimate && (roomsEstimate.participants_active > 0 || (roomsEstimate.guides?.count || 0) > 0) && (
            <div
              data-testid="rooms-estimate"
              className="rounded-lg border-2 border-[#026FCB]/30 bg-[#026FCB]/5 dark:bg-[#026FCB]/10 p-4"
            >
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[10px] mono uppercase tracking-widest text-[#026FCB]">
                    Habitaciones necesarias para este tour
                  </div>
                  <div className="text-2xl font-bold mt-0.5">
                    {roomsEstimate.total_rooms} <span className="text-sm text-slate-500 font-normal">hab. en total</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {roomsEstimate.clients.doubles > 0 && (
                    <span className="mono uppercase tracking-wider px-2 py-1 rounded bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
                      {roomsEstimate.clients.doubles} doble
                      {roomsEstimate.clients.couples > 0 && (
                        <span className="ml-1 opacity-70">(♥ {roomsEstimate.clients.couples})</span>
                      )}
                      {roomsEstimate.clients.mixed_doubles > 0 && (
                        <span className="ml-1 opacity-70">({roomsEstimate.clients.mixed_doubles} mixta cliente+guía)</span>
                      )}
                    </span>
                  )}
                  {roomsEstimate.clients.singles_total > 0 && (
                    <span className="mono uppercase tracking-wider px-2 py-1 rounded bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300">
                      {roomsEstimate.clients.singles_total} simple
                      {roomsEstimate.clients.singles_from_leftovers > 0 && (
                        <span className="ml-1 opacity-70">(+{roomsEstimate.clients.singles_from_leftovers} impar)</span>
                      )}
                    </span>
                  )}
                  {roomsEstimate.clients.triples > 0 && (
                    <span className="mono uppercase tracking-wider px-2 py-1 rounded bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                      {roomsEstimate.clients.triples} triple
                    </span>
                  )}
                  {roomsEstimate.clients.quads > 0 && (
                    <span className="mono uppercase tracking-wider px-2 py-1 rounded bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300">
                      {roomsEstimate.clients.quads} cuádruple
                    </span>
                  )}
                  {roomsEstimate.clients.quintuples > 0 && (
                    <span className="mono uppercase tracking-wider px-2 py-1 rounded bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300">
                      {roomsEstimate.clients.quintuples} quíntuple
                    </span>
                  )}
                  {roomsEstimate.clients.apartments > 0 && (
                    <span className="mono uppercase tracking-wider px-2 py-1 rounded bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300">
                      {roomsEstimate.clients.apartments} apto.
                    </span>
                  )}
                  {(roomsEstimate.guides?.count || 0) > 0 && (
                    <span className="mono uppercase tracking-wider px-2 py-1 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                      {roomsEstimate.guides.count} guía{roomsEstimate.guides.count > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                Cálculo automático: parejas explícitas primero, luego emparejamiento por mismo género (clientes y guías juntos en el mismo bucket, un guía puede compartir doble con un cliente del mismo género). Sobrantes impares → simple.
                <span className="ml-1 text-slate-400">
                  ({roomsEstimate.participants_active} clientes activos + {roomsEstimate.guides.count} guías)
                </span>
              </p>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm text-slate-500">
              {accommodations?.length || 0} hotel(es) asignado(s) a este tour
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                data-testid="tour-export-rooming-pdf"
                onClick={async () => {
                  try {
                    await downloadFile(`/tours/${id}/rooming.pdf`, `rooming_${(tour?.name || 'tour').replace(/\s+/g, '_').toLowerCase()}.pdf`);
                    toast.success("Rooming list descargado");
                  } catch (e) { toast.error("Error al generar PDF"); }
                }}
              >
                <FileDown size={16} className="mr-2" />Exportar PDF
              </Button>
              <Button
                onClick={openNewAcc}
                data-testid="tour-new-acc-btn"
                className="bg-[#026FCB] hover:bg-[#0358a5]"
              >
                <Plus size={16} className="mr-2" />Nuevo hotel
              </Button>
            </div>
          </div>
          {(accommodations || []).slice().sort((a, b) => {
            const da = a.check_in || a.date || "";
            const db = b.check_in || b.date || "";
            return String(da).localeCompare(String(db));
          }).map((a) => {
            const rts = Array.isArray(a.room_types) && a.room_types.length > 0 ? a.room_types : null;
            const nights = Number(a.nights) || 1;
            const rc = rts ? rts.reduce((s, r) => s + (Number(r.count) || 0), 0) : (a.rooms_count || 1);
            const total = rts
              ? rts.reduce((s, r) => s + (Number(r.count) || 0) * (Number(r.price) || 0) * nights, 0)
              : (a.price || 0) * rc * nights;
            const rtSummary = rts
              ? rts.filter((r) => Number(r.count) > 0).map((r) => `${r.count}× ${ROOM_TYPE[r.type] || r.type}`).join(" · ")
              : `${rc} hab.`;
            const meals = [
              a.breakfast_included ? "Desayuno" : null,
              a.lunch_included ? "Comida" : null,
              a.dinner_included ? "Cena" : null,
            ].filter(Boolean);
            return (
              <Card key={a.id} data-testid={`tour-acc-${a.id}`}>
                <CardContent
                  className="p-4 flex items-center justify-between gap-3 flex-wrap cursor-pointer hover:bg-slate-50 dark:hover:bg-[#1A2336]/40 transition-colors"
                  onClick={() => toggleAcc(a.id)}
                  role="button"
                  aria-expanded={!!expandedAccs[a.id]}
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      <span className={`inline-block transition-transform text-slate-400 text-xs ${expandedAccs[a.id] ? "rotate-90" : ""}`}>▶</span>
                      {a.hotel_name}
                    </div>
                    <div className="text-sm text-slate-500">
                      {a.city} · {a.nights} noches · {rtSummary} · check-in {fmtDate(a.check_in)} → check-out {fmtDate(a.check_out)}
                    </div>
                    {meals.length > 0 && (
                      <div className="mt-1 flex items-center gap-1 flex-wrap">
                        {meals.map((m) => (
                          <span key={m} className="text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">{m}</span>
                        ))}
                      </div>
                    )}
                    {a.notes && !expandedAccs[a.id] && (
                      <div className="mt-2 text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap bg-slate-50 dark:bg-[#1A2336]/40 rounded px-2 py-1.5 border-l-2 border-[#026FCB]/60 line-clamp-2">
                        <span className="mono uppercase text-[9px] tracking-wider text-slate-400 mr-1">Notas:</span>{a.notes}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap" onClick={(e) => e.stopPropagation()}>
                    <div className="text-right">
                      <StatusBadge value={a.confirmation_status} kind="acc" />
                      <div className="text-sm mono mt-1 font-semibold">{money(total, a.currency)}</div>
                      <div className="text-[10px] mono text-slate-400">{rc} hab.</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`tour-edit-acc-${a.id}`}
                        onClick={() => openEditAcc(a)}
                      >
                        <Pencil size={13} className="mr-1" />Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-slate-400 hover:text-red-500"
                        data-testid={`tour-del-acc-${a.id}`}
                        onClick={() => deleteAcc(a)}
                        title="Eliminar"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
                {expandedAccs[a.id] && (
                  <div className="px-4 pb-4 pt-0 border-t border-slate-100 dark:border-[#25334D]/50 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm" data-testid={`acc-details-${a.id}`}>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Hotel</div><div className="font-medium">{a.hotel_name || "—"}</div></div>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Ciudad</div><div>{a.city || "—"}</div></div>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Tipo</div><div>{a.property_type || "—"}</div></div>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Noches</div><div>{a.nights || 0}</div></div>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Check-in</div><div className="mono">{fmtDate(a.check_in)}</div></div>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Check-out</div><div className="mono">{fmtDate(a.check_out)}</div></div>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Habitaciones</div><div>{rtSummary}</div></div>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Total</div><div className="mono font-semibold text-[#026FCB]">{money(total, a.currency)}</div></div>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Estado</div><div><StatusBadge value={a.confirmation_status} kind="acc" /></div></div>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Código reserva</div><div className="mono">{a.reservation_code || "—"}</div></div>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Pagado</div><div>{a.paid ? "Sí" : "No"}</div></div>
                    <div><div className="text-[10px] mono uppercase text-slate-400">Comidas incluidas</div><div>{meals.length > 0 ? meals.join(" · ") : "—"}</div></div>
                    {a.notes && (
                      <div className="col-span-2 md:col-span-3">
                        <div className="text-[10px] mono uppercase text-slate-400">Notas</div>
                        <div className="whitespace-pre-wrap bg-slate-50 dark:bg-[#1A2336]/40 rounded px-2 py-1.5 border-l-2 border-[#026FCB]/60 mt-1">{a.notes}</div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
          {(!accommodations || accommodations.length === 0) && (
            <Card><CardContent className="p-10 text-center text-slate-500">
              Aún no hay hoteles en este tour. Pulsa <b>+ Nuevo hotel</b> para añadir el primero.
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="transport" className="mt-4 space-y-3">
          {(() => {
            const totalCap = (transports || []).reduce((s, x) => s + (Number(x.capacity_pax) || 0), 0);
            const activeStatuses = ["deposit_paid","confirmed","partial_paid","fully_paid","deposit_pending","interested","waitlist","lead"];
            const activePax = (participants || []).filter((p) => activeStatuses.includes(p.booking_status)).length;
            const totalPax = activePax + (tour.guides?.length || 0);
            const shortage = totalPax > totalCap && (transports || []).length > 0;
            return (
              <div className={`flex items-center gap-3 flex-wrap px-3 py-2 rounded-md border ${
                shortage
                  ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
                  : "border-slate-200 bg-slate-50 text-slate-600 dark:border-[#25334D] dark:bg-[#1A2336] dark:text-slate-300"
              }`}>
                <div className="text-xs mono uppercase tracking-wider">
                  Capacidad total: <b>{totalCap}</b> plazas · Pax previstos: <b>{totalPax}</b>
                  <span className="ml-2 opacity-70">({activePax} clientes + {tour.guides?.length || 0} guías)</span>
                </div>
                {shortage && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold">
                    <AlertTriangle size={13} /> Faltan {totalPax - totalCap} plaza(s)
                  </span>
                )}
              </div>
            );
          })()}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs mono uppercase tracking-wider text-slate-500">
              {transports?.length || 0} transporte(s) para este tour
            </div>
            <Dialog open={transportOpen} onOpenChange={setTransportOpen}>
              <DialogTrigger asChild>
                <Button data-testid="tour-new-transport-btn" className="bg-[#026FCB] hover:bg-[#0358a5]">
                  <Plus size={16} className="mr-2" />Nuevo transporte
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>Nuevo transporte para {tour.name}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Empresa</Label><Input data-testid="tr-company" value={transportForm.company} onChange={(e) => setTransportForm({ ...transportForm, company: e.target.value })} /></div>
                  <div><Label>Vehículo</Label><Input value={transportForm.vehicle_type} onChange={(e) => setTransportForm({ ...transportForm, vehicle_type: e.target.value })} /></div>
                  <div><Label>Matrícula</Label><Input value={transportForm.plate} onChange={(e) => setTransportForm({ ...transportForm, plate: e.target.value })} /></div>
                  <div><Label>Capacidad pax</Label><Input type="number" value={transportForm.capacity_pax} onChange={(e) => setTransportForm({ ...transportForm, capacity_pax: e.target.value })} /></div>
                  <div><Label>Recogida (lugar)</Label><Input value={transportForm.pickup_place} onChange={(e) => setTransportForm({ ...transportForm, pickup_place: e.target.value })} /></div>
                  <div><Label>Recogida (fecha)</Label><Input type="datetime-local" value={transportForm.pickup_datetime} onChange={(e) => setTransportForm({ ...transportForm, pickup_datetime: e.target.value })} /></div>
                  <div><Label>Devolución (lugar)</Label><Input value={transportForm.return_place} onChange={(e) => setTransportForm({ ...transportForm, return_place: e.target.value })} /></div>
                  <div><Label>Devolución (fecha)</Label><Input type="datetime-local" value={transportForm.return_datetime} onChange={(e) => setTransportForm({ ...transportForm, return_datetime: e.target.value })} /></div>
                  <div><Label>Precio</Label><Input type="number" value={transportForm.price} onChange={(e) => setTransportForm({ ...transportForm, price: e.target.value })} /></div>
                  <div><Label>Estado</Label>
                    <Select value={transportForm.status} onValueChange={(v) => setTransportForm({ ...transportForm, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["draft","in_progress","confirmed","cancelled"].map((s) => (
                          <SelectItem key={s} value={s}>{TRANSPORT_STATUS[s] || s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Notas</Label><Input value={transportForm.notes} onChange={(e) => setTransportForm({ ...transportForm, notes: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setTransportOpen(false)}>Cancelar</Button>
                  <Button data-testid="tr-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={submitTransport}>Guardar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {(transports || []).map((t) => {
            const isOpen = !!expandedTransports[t.id];
            const fmtDT = (s) => {
              if (!s) return "—";
              try { const d = new Date(s); return isNaN(d.getTime()) ? s : d.toLocaleString("es", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; }
            };
            return (
            <Card key={t.id} data-testid={`transport-card-${t.id}`}>
              <CardContent
                className="p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-[#1A2336]/40 transition-colors"
                onClick={() => toggleTransport(t.id)}
                role="button"
                aria-expanded={isOpen}
              >
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate flex items-center gap-2">
                  <span className={`inline-block transition-transform text-slate-400 ${isOpen ? "rotate-90" : ""}`}>▶</span>
                  {t.company} · {t.vehicle_type}
                </div>
                <div className="text-sm text-slate-500 truncate">Recogida {t.pickup_place || "—"} · {t.capacity_pax} pax</div>
                {t.plate && <div className="text-[10px] mono uppercase text-slate-400 mt-0.5">{t.plate}</div>}
              </div>
              <div className="text-right shrink-0">
                <StatusBadge value={t.status} />
                <div className="text-sm mono mt-1">{money(t.price)}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`edit-transport-${t.id}`}
                  onClick={() => openEditTransport(t)}
                  title="Editar transporte"
                  className="h-8 w-8 p-0"
                >
                  <Pencil size={13} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`duplicate-transport-${t.id}`}
                  onClick={() => duplicateTransport(t)}
                  title="Duplicar transporte"
                  className="h-8 w-8 p-0"
                >
                  <Copy size={13} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`delete-transport-${t.id}`}
                  onClick={async () => {
                    if (!window.confirm(`¿Eliminar el transporte "${t.company || "sin nombre"} · ${t.vehicle_type || ""}"?`)) return;
                    try {
                      await api.delete(`/transports/${t.id}`);
                      toast.success("Transporte eliminado");
                      mutateTransports();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Error al eliminar");
                    }
                  }}
                  title="Eliminar transporte"
                  className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30"
                >
                  <Trash2 size={13} />
                </Button>
              </div>
              </CardContent>
              {isOpen && (
                <div className="px-4 pb-4 pt-0 border-t border-slate-100 dark:border-[#25334D]/50 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm" data-testid={`transport-details-${t.id}`}>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Empresa</div><div className="font-medium">{t.company || "—"}</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Vehículo</div><div>{t.vehicle_type || "—"}</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Matrícula</div><div className="mono">{t.plate || "—"}</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Capacidad</div><div>{t.capacity_pax || 0} pax</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Precio</div><div className="mono font-semibold text-[#026FCB]">{money(t.price)}</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Estado</div><div><StatusBadge value={t.status} /></div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Recogida (lugar)</div><div>{t.pickup_place || "—"}</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Recogida (fecha)</div><div className="mono">{fmtDT(t.pickup_datetime)}</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Devolución (lugar)</div><div>{t.return_place || "—"}</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Devolución (fecha)</div><div className="mono">{fmtDT(t.return_datetime)}</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Combustible incluido</div><div>{t.fuel_included ? "Sí" : "No"}</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Seguro</div><div>{t.insurance ? "Sí" : "No"}</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Depósito</div><div className="mono">{t.deposit ? money(t.deposit) : "—"}</div></div>
                  <div><div className="text-[10px] mono uppercase text-slate-400">Código reserva</div><div className="mono">{t.reservation_code || "—"}</div></div>
                  {t.notes && (
                    <div className="col-span-2 md:col-span-3">
                      <div className="text-[10px] mono uppercase text-slate-400">Notas</div>
                      <div className="whitespace-pre-wrap bg-slate-50 dark:bg-[#1A2336]/40 rounded px-2 py-1.5 border-l-2 border-[#026FCB]/60 mt-1">{t.notes}</div>
                    </div>
                  )}
                </div>
              )}
            </Card>
            );
          })}
          {(transports || []).length === 0 && (
            <Card><CardContent className="p-10 text-center text-slate-500">Sin transporte registrado todavía.</CardContent></Card>
          )}

          {/* Editar transporte dialog */}
          <Dialog open={!!editTransportId} onOpenChange={(o) => !o && setEditTransportId(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Editar transporte</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Empresa</Label><Input data-testid="edit-tr-company" value={editTransportForm.company} onChange={(e) => setEditTransportForm({ ...editTransportForm, company: e.target.value })} /></div>
                <div><Label>Vehículo</Label><Input value={editTransportForm.vehicle_type} onChange={(e) => setEditTransportForm({ ...editTransportForm, vehicle_type: e.target.value })} /></div>
                <div><Label>Matrícula</Label><Input value={editTransportForm.plate} onChange={(e) => setEditTransportForm({ ...editTransportForm, plate: e.target.value })} /></div>
                <div><Label>Capacidad pax</Label><Input type="number" value={editTransportForm.capacity_pax} onChange={(e) => setEditTransportForm({ ...editTransportForm, capacity_pax: e.target.value })} /></div>
                <div><Label>Recogida (lugar)</Label><Input value={editTransportForm.pickup_place} onChange={(e) => setEditTransportForm({ ...editTransportForm, pickup_place: e.target.value })} /></div>
                <div><Label>Recogida (fecha)</Label><Input type="datetime-local" value={editTransportForm.pickup_datetime} onChange={(e) => setEditTransportForm({ ...editTransportForm, pickup_datetime: e.target.value })} /></div>
                <div><Label>Devolución (lugar)</Label><Input value={editTransportForm.return_place} onChange={(e) => setEditTransportForm({ ...editTransportForm, return_place: e.target.value })} /></div>
                <div><Label>Devolución (fecha)</Label><Input type="datetime-local" value={editTransportForm.return_datetime} onChange={(e) => setEditTransportForm({ ...editTransportForm, return_datetime: e.target.value })} /></div>
                <div><Label>Precio</Label><Input type="number" value={editTransportForm.price} onChange={(e) => setEditTransportForm({ ...editTransportForm, price: e.target.value })} /></div>
                <div><Label>Estado</Label>
                  <Select value={editTransportForm.status} onValueChange={(v) => setEditTransportForm({ ...editTransportForm, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["draft","in_progress","confirmed","cancelled"].map((s) => (
                        <SelectItem key={s} value={s}>{TRANSPORT_STATUS[s] || s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Notas</Label><Input value={editTransportForm.notes} onChange={(e) => setEditTransportForm({ ...editTransportForm, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditTransportId(null)}>Cancelar</Button>
                <Button data-testid="edit-tr-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={saveEditTransport}>Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="expenses" className="mt-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs mono uppercase tracking-wider text-slate-500">
              Duración del tour: <span className="text-slate-700 dark:text-slate-300 font-semibold">{tourDays || 0} día(s)</span>
              {(tour.guides?.length || 0) > 0 && (
                <span className="ml-3">
                  · {tour.guides.length} guía(s) asignado(s)
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                data-testid="tour-export-expenses-pdf"
                onClick={async () => {
                  try {
                    await downloadFile(`/tours/${id}/expenses.pdf`, `gastos_${(tour?.name || 'tour').replace(/\s+/g, '_').toLowerCase()}.pdf`);
                    toast.success("Hoja de gastos descargada");
                  } catch (e) { toast.error("Error al generar PDF"); }
                }}
              >
                <FileDown size={16} className="mr-2" />Exportar PDF
              </Button>
              <Button
                variant="outline"
                onClick={openPerDiem}
                disabled={(tour.guides?.length || 0) === 0 || tourDays === 0}
                data-testid="tour-per-diem-btn"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
                title={(tour.guides?.length || 0) === 0 ? "Asigna guías al tour primero" : "Calcular dietas de guías"}
              >
                <DollarSign size={16} className="mr-1" />Dietas de guías
              </Button>
              <Dialog open={expOpen} onOpenChange={setExpOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="tour-new-expense-btn" className="bg-[#026FCB] hover:bg-[#0358a5]"><Plus size={16} className="mr-2" />Nuevo gasto</Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nuevo gasto — {tour?.name}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2"><Label>Concepto</Label><Input data-testid="tour-exp-concept" value={expForm.concept} onChange={(e) => setExpForm({...expForm, concept: e.target.value})} /></div>
                  <div><Label>Categoría</Label>
                    <Select value={expForm.category} onValueChange={(v) => setExpForm({...expForm, category: v})}>
                      <SelectTrigger data-testid="tour-exp-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{EXP_CATS.map((c) => <SelectItem key={c} value={c}>{EXP_CATEGORY[c] || c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Tipo de coste</Label>
                    <Select value={expForm.cost_type} onValueChange={(v) => setExpForm({...expForm, cost_type: v})}>
                      <SelectTrigger data-testid="tour-exp-cost-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="global">Global (fijo del tour)</SelectItem>
                        <SelectItem value="per_person">Por persona (escala con pax)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Fecha</Label><DateInputES value={expForm.date} onChange={(e) => setExpForm({...expForm, date: e.target.value})} /></div>
                  <div><Label>Precio unitario (USD)</Label><Input type="number" step="0.01" data-testid="tour-exp-unit" value={expForm.unit_amount} onChange={(e) => setExpForm({...expForm, unit_amount: e.target.value})} /></div>
                  <div><Label>Cantidad</Label><Input type="number" step="1" min="1" data-testid="tour-exp-qty" value={expForm.quantity} onChange={(e) => setExpForm({...expForm, quantity: e.target.value})} /></div>
                  <div className="col-span-2">
                    <Label>Total (USD)</Label>
                    <div data-testid="tour-exp-total" className="mt-1 px-3 py-2 rounded border border-slate-200 dark:border-[#25334D] bg-slate-50 dark:bg-[#0F1A2E] mono font-semibold text-[#026FCB]">
                      {(() => {
                        const u = Number(expForm.unit_amount) || 0;
                        const q = Number(expForm.quantity) || 0;
                        return `US$ ${(u * q).toFixed(2)}  ·  ${u.toFixed(2)} × ${q || 0}`;
                      })()}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setExpOpen(false)}>Cancelar</Button>
                  <Button data-testid="tour-exp-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={saveExpense}>Guardar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          </div>
          <Card className="overflow-hidden"><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-100 dark:bg-[#1A2336]">
                <tr>
                  <th className="text-left px-4 py-3">Concepto</th>
                  <th className="text-left px-4 py-3">Categoría</th>
                  <th className="text-right px-4 py-3">Importe</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {/* Filas sintéticas auto-calculadas desde hoteles y transportes */}
                {(() => {
                  const nightsFor = (a) => Number(a.nights) || 1;
                  const hotelsTotal = (accommodations || []).reduce((s, a) => {
                    const rc = Number(a.rooms_count) || (Array.isArray(a.room_types) ? a.room_types.reduce((x, r) => x + (Number(r.count) || 0), 0) : 1);
                    return s + (Number(a.price) || 0) * rc * nightsFor(a);
                  }, 0);
                  const transportsTotal = (transports || []).reduce((s, t) => s + (Number(t.price) || 0), 0);
                  return (
                    <>
                      {hotelsTotal > 0 && (
                        <tr className="border-t border-slate-100 dark:border-[#25334D]/50 bg-sky-50/60 dark:bg-sky-950/20" data-testid="synthetic-hotels-row">
                          <td className="px-4 py-3 font-medium">
                            <span className="inline-flex items-center gap-1.5">🏨 Hoteles (auto)
                              <span className="text-[9px] mono uppercase tracking-wider px-1 py-0.5 rounded bg-sky-200/70 text-sky-800 dark:bg-sky-900 dark:text-sky-200">Automático</span>
                            </span>
                            <div className="text-[10px] text-slate-500 mt-0.5">{(accommodations || []).length} hotel(es) · calculado desde la pestaña Hoteles</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span>Alojamiento</span>
                              <span className="text-[9px] mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">global</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right mono font-semibold">{money(hotelsTotal)}</td>
                          <td className="px-4 py-3 text-right text-[10px] text-slate-400">—</td>
                        </tr>
                      )}
                      {transportsTotal > 0 && (
                        <tr className="border-t border-slate-100 dark:border-[#25334D]/50 bg-amber-50/60 dark:bg-amber-950/20" data-testid="synthetic-transports-row">
                          <td className="px-4 py-3 font-medium">
                            <span className="inline-flex items-center gap-1.5">🚐 Transportes (auto)
                              <span className="text-[9px] mono uppercase tracking-wider px-1 py-0.5 rounded bg-amber-200/70 text-amber-800 dark:bg-amber-900 dark:text-amber-200">Automático</span>
                            </span>
                            <div className="text-[10px] text-slate-500 mt-0.5">{(transports || []).length} transporte(s) · calculado desde la pestaña Transporte</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span>Vehículos</span>
                              <span className="text-[9px] mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">global</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right mono font-semibold">{money(transportsTotal)}</td>
                          <td className="px-4 py-3 text-right text-[10px] text-slate-400">—</td>
                        </tr>
                      )}
                      {flightsGrandTotal > 0 && (
                        <tr className="border-t border-slate-100 dark:border-[#25334D]/50 bg-sky-50/60 dark:bg-sky-950/20" data-testid="synthetic-flights-row">
                          <td className="px-4 py-3 font-medium">
                            <span className="inline-flex items-center gap-1.5">✈️ Vuelos (auto)
                              <span className="text-[9px] mono uppercase tracking-wider px-1 py-0.5 rounded bg-sky-200/70 text-sky-800 dark:bg-sky-900 dark:text-sky-200">Automático</span>
                            </span>
                            <div className="text-[10px] text-slate-500 mt-0.5">{(flights || []).length - flightsPendingCount} vuelo(s) con precio · calculado desde la pestaña Vuelos (incluye escalas)</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span>Vuelos</span>
                              <span className="text-[9px] mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">global</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right mono font-semibold">{money(flightsGrandTotal)}</td>
                          <td className="px-4 py-3 text-right text-[10px] text-slate-400">—</td>
                        </tr>
                      )}
                      {flightsPendingCount > 0 && (
                        <tr className="border-t border-slate-100 dark:border-[#25334D]/50 bg-yellow-50/60 dark:bg-yellow-950/20" data-testid="synthetic-flights-pending-row">
                          <td className="px-4 py-3 font-medium">
                            <span className="inline-flex items-center gap-1.5">✈️ Vuelos pendientes
                              <span className="text-[9px] mono uppercase tracking-wider px-1 py-0.5 rounded bg-yellow-200/80 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Incompleto</span>
                            </span>
                            <div className="text-[10px] text-yellow-800 dark:text-yellow-300 mt-0.5">
                              {flightsPendingCount} vuelo(s) sin precio real · NO se suma al total de gastos — completa el importe en la pestaña Vuelos
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span>Vuelos</span>
                              <span className="text-[9px] mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">pendiente</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right mono font-semibold text-yellow-700 dark:text-yellow-400">
                            {flightsPendingEstimate > 0 ? `~ ${money(flightsPendingEstimate)}` : "—"}
                          </td>
                          <td className="px-4 py-3 text-right text-[10px] text-slate-400">—</td>
                        </tr>
                      )}
                    </>
                  );
                })()}
                {(expenses || []).map((e) => {
                  const pax = Number(finance?.pax_used_for_calc || 0);
                  const rawAmt = Number(e.amount) || 0;
                  const rate = Number(e.exchange_rate) || 1;
                  const inUSD = e.currency === "USD" ? rawAmt : rawAmt * rate;
                  const scaled = e.cost_type === "per_person" ? inUSD * pax : inUSD;
                  return (
                  <tr key={e.id} className="border-t border-slate-100 dark:border-[#25334D]/50">
                    <td className="px-4 py-3">{e.concept}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span>{EXP_CATEGORY[e.category] || e.category}</span>
                        <span
                          data-testid={`exp-cost-type-${e.id}`}
                          className={`text-[9px] mono uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            e.cost_type === "per_person"
                              ? "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                          }`}
                        >
                          {e.cost_type === "per_person" ? "×pax" : "global"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right mono">
                      {money(scaled)}
                      {(() => {
                        const u = Number(e.unit_amount);
                        const q = Number(e.quantity);
                        if (Number.isFinite(u) && u > 0 && Number.isFinite(q) && q > 1) {
                          return (
                            <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`exp-breakdown-${e.id}`}>
                              {money(u)} × {q}
                            </div>
                          );
                        }
                        return null;
                      })()}
                      {e.cost_type === "per_person" && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {money(inUSD)} × {pax} pax
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-exp-${e.id}`}
                          onClick={() => openEditExp(e)}
                        >
                          <Pencil size={13} className="mr-1" />Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 text-slate-400 hover:text-red-500"
                          data-testid={`del-exp-${e.id}`}
                          onClick={() => deleteExpense(e)}
                          title="Eliminar gasto"
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );})}
                {(!expenses || expenses.length === 0) && (accommodations || []).length === 0 && (transports || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center py-10 text-slate-500">Sin gastos aún. Añade el primero.</td></tr>
                )}
              </tbody>
              <tfoot>
                {(() => {
                  const nightsFor = (a) => Number(a.nights) || 1;
                  const hotelsTotal = (accommodations || []).reduce((s, a) => {
                    const rc = Number(a.rooms_count) || (Array.isArray(a.room_types) ? a.room_types.reduce((x, r) => x + (Number(r.count) || 0), 0) : 1);
                    return s + (Number(a.price) || 0) * rc * nightsFor(a);
                  }, 0);
                  const transportsTotal = (transports || []).reduce((s, t) => s + (Number(t.price) || 0), 0);
                  const flightsTotalRow = (flights || []).reduce((s, f) => s + Number(f.price || 0) + (f.layovers || []).reduce((ss, l) => ss + Number(l.price || 0), 0), 0);
                  const otherTotal = (expenses || []).reduce((s, e) => {
                    const rate = Number(e.exchange_rate) || 1;
                    const amt = Number(e.amount) || 0;
                    const inUSD = e.currency === "USD" ? amt : amt * rate;
                    const pax = Number(finance?.pax_used_for_calc || 0);
                    const scaled = e.cost_type === "per_person" ? inUSD * pax : inUSD;
                    return s + scaled;
                  }, 0);
                  const grand = hotelsTotal + transportsTotal + flightsTotalRow + otherTotal;
                  return (
                    <tr className="border-t-2 border-[#026FCB]/40 bg-slate-100 dark:bg-[#1A2336]" data-testid="expenses-total-row">
                      <td className="px-4 py-3 mono uppercase text-xs tracking-widest text-[#026FCB] font-bold">Total gastos estimados</td>
                      <td className="px-4 py-3 text-[10px] text-slate-500">
                        Hoteles {money(hotelsTotal)} · Transportes {money(transportsTotal)} · Vuelos {money(flightsTotalRow)} · Otros {money(otherTotal)}
                      </td>
                      <td className="px-4 py-3 text-right mono font-bold text-base text-[#026FCB]">{money(grand)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="flights" className="mt-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-slate-500">
              {(flights?.length || 0)} vuelo(s) · Total <span className="mono font-semibold text-[#026FCB]">{money(flightsGrandTotal)}</span> · Se suma automáticamente a los gastos del tour.
            </div>
            <Button data-testid="tour-new-flight-btn" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={openNewFlight}>
              <Plus size={16} className="mr-2" />Nuevo vuelo
            </Button>
          </div>
          {(flights || []).map((f) => {
            const isOpen = !!expandedFlights[f.id];
            return (
            <Card key={f.id} data-testid={`flight-card-${f.id}`}>
              <CardContent
                className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-[#1A2336]/40 transition-colors"
                onClick={() => toggleFlight(f.id)}
                role="button"
                aria-expanded={isOpen}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-base flex items-center gap-2 flex-wrap">
                      <span className={`inline-block transition-transform text-slate-400 text-xs ${isOpen ? "rotate-90" : ""}`}>▶</span>
                      <span className="text-lg">✈️</span>
                      <span>{f.airline || "—"}</span>
                      {f.flight_number && <span className="text-xs mono uppercase text-slate-500">{f.flight_number}</span>}
                      {(f.layovers || []).length > 0 && (
                        <span className="text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                          {(f.layovers || []).length + 1} tramo(s)
                        </span>
                      )}
                      {flightIsPending(f) && (
                        <span
                          data-testid={`flight-pending-badge-${f.id}`}
                          title="Precio incompleto — edita el vuelo y añade el importe real"
                          className="text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700"
                        >
                          Precio pendiente
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      {f.from_airport && f.to_airport ? `${f.from_airport} → ${f.to_airport}` : (f.from_airport || f.to_airport || "")}
                      {(f.date || f.time) && (<span> · {f.date ? fmtDate(f.date) : ""} {f.time ? f.time : ""}</span>)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] mono uppercase tracking-wider text-slate-500">Total vuelo</div>
                    <div className={`text-lg mono font-bold ${flightIsPending(f) ? "text-yellow-700 dark:text-yellow-400" : "text-[#026FCB]"}`}>
                      {flightIsPending(f) ? "Pendiente" : money(flightTotal(f))}
                    </div>
                    {flightIsPending(f) && (
                      <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`flight-pending-hint-${f.id}`}>
                        No suma en gastos hasta completar
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-2 justify-end" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" data-testid={`edit-flight-${f.id}`} onClick={() => openEditFlight(f)} title="Editar vuelo" className="h-8 w-8 p-0">
                        <Pencil size={13} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`delete-flight-${f.id}`}
                        onClick={async () => {
                          if (!window.confirm(`¿Eliminar el vuelo de "${f.airline || "sin compañía"}"?`)) return;
                          try {
                            await api.delete(`/flights/${f.id}`);
                            toast.success("Vuelo eliminado");
                            mutateFlights();
                            mutateFinance && mutateFinance();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Error al eliminar");
                          }
                        }}
                        title="Eliminar vuelo"
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30"
                      >
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
              {isOpen && (
                <div className="px-4 pb-4 pt-0 border-t border-slate-100 dark:border-[#25334D]/50" data-testid={`flight-details-${f.id}`}>
                  <div className="pt-3">
                    <div className="text-[10px] mono uppercase tracking-wider text-slate-500 font-semibold mb-2">Vuelo principal</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                      <div><div className="text-[10px] mono uppercase text-slate-400">Compañía</div><div className="font-medium">{f.airline || "—"}</div></div>
                      <div><div className="text-[10px] mono uppercase text-slate-400">Nº de vuelo</div><div className="mono">{f.flight_number || "—"}</div></div>
                      <div><div className="text-[10px] mono uppercase text-slate-400">Precio</div><div className="mono font-semibold text-[#026FCB]">{money(f.price)}</div></div>
                      <div><div className="text-[10px] mono uppercase text-slate-400">Fecha</div><div className="mono">{f.date ? fmtDate(f.date) : "—"}</div></div>
                      <div><div className="text-[10px] mono uppercase text-slate-400">Hora</div><div className="mono">{f.time || "—"}</div></div>
                      <div><div className="text-[10px] mono uppercase text-slate-400">Ruta</div><div className="mono">{(f.from_airport || "—") + " → " + (f.to_airport || "—")}</div></div>
                    </div>
                  </div>
                  {(f.layovers || []).length > 0 && (
                    <div className="mt-4 space-y-3">
                      {(f.layovers || []).map((l, i) => (
                        <div key={i} className="border-l-2 border-amber-400 pl-3">
                          <div className="text-[10px] mono uppercase tracking-wider text-amber-600 font-semibold mb-2">Escala {i + 1}</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                            <div><div className="text-[10px] mono uppercase text-slate-400">Compañía</div><div className="font-medium">{l.airline || "—"}</div></div>
                            <div><div className="text-[10px] mono uppercase text-slate-400">Nº de vuelo</div><div className="mono">{l.flight_number || "—"}</div></div>
                            <div><div className="text-[10px] mono uppercase text-slate-400">Precio</div><div className="mono font-semibold">{money(l.price || 0)}</div></div>
                            <div><div className="text-[10px] mono uppercase text-slate-400">Fecha</div><div className="mono">{l.date ? fmtDate(l.date) : "—"}</div></div>
                            <div><div className="text-[10px] mono uppercase text-slate-400">Hora</div><div className="mono">{l.time || "—"}</div></div>
                            <div><div className="text-[10px] mono uppercase text-slate-400">Ruta</div><div className="mono">{(l.from_airport || "—") + " → " + (l.to_airport || "—")}</div></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {f.notes && (
                    <div className="mt-3">
                      <div className="text-[10px] mono uppercase text-slate-400">Notas</div>
                      <div className="whitespace-pre-wrap bg-slate-50 dark:bg-[#1A2336]/40 rounded px-2 py-1.5 border-l-2 border-[#026FCB]/60 mt-1 text-sm">{f.notes}</div>
                    </div>
                  )}
                </div>
              )}
            </Card>
            );
          })}
          {(flights || []).length === 0 && (
            <Card><CardContent className="p-10 text-center text-slate-500">Sin vuelos registrados. Añade el primero.</CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="itinerary" className="mt-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-slate-500">
              {(itinerary?.length || 0)} día(s) · Los días se generan automáticamente según las fechas del tour.
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="itinerary-add-day-btn"
                onClick={() => { setAddDayDate(""); setAddDayOpen(true); }}
              >
                <Plus size={14} className="mr-1" /> Añadir día
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="itinerary-regenerate-btn"
                onClick={async () => {
                  try {
                    await api.post(`/itinerary/auto-generate/${id}`);
                    await mutateItinerary();
                    toast.success("Días sincronizados con las fechas del tour");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Error al regenerar días");
                  }
                }}
              >
                Regenerar días
              </Button>
            </div>
          </div>

          {(itinerary || []).sort((a,b) => a.day_number - b.day_number).map((d) => (
            <ItineraryDayCard
              key={d.id}
              day={d}
              accommodations={accommodations || []}
              onSaved={mutateItinerary}
              onDeleted={mutateItinerary}
            />
          ))}

          {(!itinerary || itinerary.length === 0) && (
            <Card><CardContent className="p-10 text-center text-slate-500">
              {tour?.start_date && tour?.end_date
                ? "Generando días del itinerario..."
                : "Este tour no tiene fechas definidas. Añade fecha de inicio y fin para generar el itinerario."}
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Nuevo/Editar vuelo dialog */}
      <Dialog open={flightOpen} onOpenChange={setFlightOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="flight-dialog">
          <DialogHeader>
            <DialogTitle>{editFlightId ? "Editar vuelo" : "Nuevo vuelo"}</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">El precio del vuelo y de las escalas se suma automáticamente a los gastos del tour.</p>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 dark:border-[#25334D]/60 p-3 space-y-3">
              <div className="text-xs mono uppercase tracking-wider text-slate-500 font-semibold">Vuelo principal</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Compañía *</Label><Input data-testid="flight-airline" value={flightForm.airline} onChange={(e) => setFlightForm({ ...flightForm, airline: e.target.value })} placeholder="Ej. Iberia" /></div>
                <div><Label>Nº de vuelo</Label><Input value={flightForm.flight_number} onChange={(e) => setFlightForm({ ...flightForm, flight_number: e.target.value })} placeholder="IB6845" /></div>
                <div><Label>Precio (USD)</Label><Input type="number" data-testid="flight-price" value={flightForm.price} onChange={(e) => setFlightForm({ ...flightForm, price: e.target.value })} /></div>
                <div><Label>Fecha</Label><DateInputES value={flightForm.date} onChange={(e) => setFlightForm({ ...flightForm, date: e.target.value })} /></div>
                <div><Label>Hora</Label><Input type="time" data-testid="flight-time" value={flightForm.time} onChange={(e) => setFlightForm({ ...flightForm, time: e.target.value })} /></div>
                <div><Label>Origen</Label><Input value={flightForm.from_airport} onChange={(e) => setFlightForm({ ...flightForm, from_airport: e.target.value })} placeholder="MAD" /></div>
                <div><Label>Destino</Label><Input value={flightForm.to_airport} onChange={(e) => setFlightForm({ ...flightForm, to_airport: e.target.value })} placeholder="KEF" /></div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="flight-has-stopover"
                type="checkbox"
                data-testid="flight-has-stopover"
                checked={!!flightForm.has_stopover}
                onChange={(e) => setFlightForm({
                  ...flightForm,
                  has_stopover: e.target.checked,
                  layovers: e.target.checked && (flightForm.layovers || []).length === 0
                    ? [{ ...emptyFlightLeg }]
                    : (flightForm.layovers || []),
                })}
                className="accent-[#026FCB] w-4 h-4"
              />
              <label htmlFor="flight-has-stopover" className="text-sm font-medium cursor-pointer">Este vuelo tiene escala(s)</label>
            </div>

            {flightForm.has_stopover && (flightForm.layovers || []).map((leg, idx) => (
              <div key={idx} className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-950/10 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs mono uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold">Escala {idx + 1}</div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-red-600 hover:text-red-700"
                    data-testid={`flight-remove-layover-${idx}`}
                    onClick={() => setFlightForm({
                      ...flightForm,
                      layovers: flightForm.layovers.filter((_, i) => i !== idx),
                    })}
                  >
                    <X size={13} className="mr-1" />Quitar
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Compañía</Label><Input value={leg.airline || ""} onChange={(e) => {
                    const list = [...flightForm.layovers]; list[idx] = { ...list[idx], airline: e.target.value }; setFlightForm({ ...flightForm, layovers: list });
                  }} placeholder="Ej. Air Europa" /></div>
                  <div><Label>Nº de vuelo</Label><Input value={leg.flight_number || ""} onChange={(e) => {
                    const list = [...flightForm.layovers]; list[idx] = { ...list[idx], flight_number: e.target.value }; setFlightForm({ ...flightForm, layovers: list });
                  }} /></div>
                  <div><Label>Precio (USD)</Label><Input type="number" data-testid={`flight-layover-price-${idx}`} value={leg.price || 0} onChange={(e) => {
                    const list = [...flightForm.layovers]; list[idx] = { ...list[idx], price: e.target.value }; setFlightForm({ ...flightForm, layovers: list });
                  }} /></div>
                  <div><Label>Fecha</Label><DateInputES value={leg.date || ""} onChange={(e) => {
                    const list = [...flightForm.layovers]; list[idx] = { ...list[idx], date: e.target.value }; setFlightForm({ ...flightForm, layovers: list });
                  }} /></div>
                  <div><Label>Hora</Label><Input type="time" value={leg.time || ""} onChange={(e) => {
                    const list = [...flightForm.layovers]; list[idx] = { ...list[idx], time: e.target.value }; setFlightForm({ ...flightForm, layovers: list });
                  }} /></div>
                  <div><Label>Origen</Label><Input value={leg.from_airport || ""} onChange={(e) => {
                    const list = [...flightForm.layovers]; list[idx] = { ...list[idx], from_airport: e.target.value }; setFlightForm({ ...flightForm, layovers: list });
                  }} /></div>
                  <div><Label>Destino</Label><Input value={leg.to_airport || ""} onChange={(e) => {
                    const list = [...flightForm.layovers]; list[idx] = { ...list[idx], to_airport: e.target.value }; setFlightForm({ ...flightForm, layovers: list });
                  }} /></div>
                </div>
              </div>
            ))}

            {flightForm.has_stopover && (flightForm.layovers || []).length < 2 && (
              <Button
                variant="outline"
                size="sm"
                data-testid="flight-add-layover-btn"
                onClick={() => setFlightForm({
                  ...flightForm,
                  layovers: [...(flightForm.layovers || []), { ...emptyFlightLeg }],
                })}
              >
                <Plus size={14} className="mr-1" />Añadir otra escala (máx. 2)
              </Button>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFlightOpen(false)}>Cancelar</Button>
            <Button data-testid="flight-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={submitFlight} disabled={savingFlight}>
              {savingFlight ? "Guardando…" : (editFlightId ? "Guardar cambios" : "Guardar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add-day (itinerary) dialog */}
      <Dialog open={addDayOpen} onOpenChange={setAddDayOpen}>
        <DialogContent className="max-w-md" data-testid="add-day-dialog">
          <DialogHeader>
            <DialogTitle>Añadir día al itinerario</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Elige la fecha del nuevo día. Se insertará en su posición cronológica y los demás días se renumerarán automáticamente. Puedes añadir días fuera del rango del tour.
            </p>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Fecha del nuevo día</Label>
              <DateInputES
                data-testid="add-day-date"
                value={addDayDate}
                onChange={(e) => setAddDayDate(e?.target ? e.target.value : e)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDayOpen(false)}>Cancelar</Button>
            <Button
              data-testid="add-day-save"
              className="bg-[#026FCB] hover:bg-[#0358a5]"
              disabled={!addDayDate || addingDay}
              onClick={async () => {
                if (!addDayDate) return toast.error("Selecciona una fecha");
                setAddingDay(true);
                try {
                  await api.post(`/itinerary/insert-day/${id}`, { date: addDayDate });
                  await mutateItinerary();
                  toast.success("Día añadido al itinerario");
                  setAddDayOpen(false);
                  setAddDayDate("");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Error al añadir día");
                } finally {
                  setAddingDay(false);
                }
              }}
            >
              {addingDay ? "Añadiendo…" : "Añadir día"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit-participant dialog */}
      <Dialog open={editPartOpen} onOpenChange={setEditPartOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-part-dialog">
          <DialogHeader>
            <DialogTitle>Editar participante</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              <b>Datos personales</b>: se aplican a todas las reservas del cliente. Los <b>tours</b> se editan individualmente.
            </p>
          </DialogHeader>
          <Tabs value={activePartTab} onValueChange={setActivePartTab}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="__personal__" data-testid="ep-tab-personal">Datos personales</TabsTrigger>
              {editBookings.map((b) => (
                <TabsTrigger key={b.participant_id} value={b.participant_id} data-testid={`ep-tab-${b.participant_id}`}>
                  {b.tour_name}
                  {b.tour_start && <span className="ml-2 text-[10px] mono opacity-60">{fmtDate(b.tour_start)}</span>}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="__personal__" className="mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre *</Label><Input data-testid="ep-first" value={editPartForm.first_name || ""} onChange={(e) => setEditPartForm({ ...editPartForm, first_name: e.target.value })} /></div>
                <div><Label>Apellidos *</Label><Input data-testid="ep-last" value={editPartForm.last_name || ""} onChange={(e) => setEditPartForm({ ...editPartForm, last_name: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={editPartForm.email || ""} disabled className="bg-slate-50 dark:bg-[#1A2336] text-slate-500" /></div>
                <div><Label>Teléfono</Label><Input value={editPartForm.phone || ""} onChange={(e) => setEditPartForm({ ...editPartForm, phone: e.target.value })} /></div>
                <div><Label>País</Label><Input value={editPartForm.country || ""} onChange={(e) => setEditPartForm({ ...editPartForm, country: e.target.value })} /></div>
                <div><Label>Género</Label>
                  <Select value={editPartForm.gender || "__none__"} onValueChange={(v) => setEditPartForm({ ...editPartForm, gender: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="ep-gender"><SelectValue placeholder="Sin especificar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin especificar —</SelectItem>
                      <SelectItem value="male">Hombre</SelectItem>
                      <SelectItem value="female">Mujer</SelectItem>
                      <SelectItem value="other">Otro</SelectItem>
                      <SelectItem value="prefer_not_to_say">Prefiere no decirlo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Pasaporte</Label><Input value={editPartForm.passport_number || ""} onChange={(e) => setEditPartForm({ ...editPartForm, passport_number: e.target.value })} /></div>
                <div><Label>Caduca</Label><DateInputES value={editPartForm.passport_expiry || ""} onChange={(e) => setEditPartForm({ ...editPartForm, passport_expiry: e.target.value })} /></div>
                <div><Label>Fecha de nacimiento</Label><DateInputES value={editPartForm.birthdate || ""} onChange={(e) => setEditPartForm({ ...editPartForm, birthdate: e.target.value })} /></div>
                <div><Label>Persona de contacto</Label><Input placeholder="Nombre y parentesco" value={editPartForm.emergency_contact || ""} onChange={(e) => setEditPartForm({ ...editPartForm, emergency_contact: e.target.value })} /></div>
                <div><Label>Teléfono de contacto</Label><Input placeholder="+34 600 000 000" value={editPartForm.emergency_contact_phone || ""} onChange={(e) => setEditPartForm({ ...editPartForm, emergency_contact_phone: e.target.value })} /></div>
                <div className="col-span-2"><Label>Dieta</Label><Input value={editPartForm.dietary || ""} onChange={(e) => setEditPartForm({ ...editPartForm, dietary: e.target.value })} /></div>
                <div className="col-span-2"><Label>Alergias</Label><Input value={editPartForm.allergies || ""} onChange={(e) => setEditPartForm({ ...editPartForm, allergies: e.target.value })} /></div>
                <div className="col-span-2"><Label>Notas médicas</Label><Input value={editPartForm.medical || ""} onChange={(e) => setEditPartForm({ ...editPartForm, medical: e.target.value })} /></div>
              </div>
            </TabsContent>

            {editBookings.map((b) => {
              const r = editReservas[b.participant_id] || {};
              const setR = (patch) => setEditReservas((prev) => ({ ...prev, [b.participant_id]: { ...prev[b.participant_id], ...patch } }));
              return (
                <TabsContent key={b.participant_id} value={b.participant_id} className="mt-4">
                  <div className="mb-3 p-2 rounded-md bg-sky-50 dark:bg-sky-950/30 text-xs text-sky-800 dark:text-sky-300">
                    Editando reserva de <b>{b.tour_name}</b>{b.tour_start && <> · Inicio {fmtDate(b.tour_start)}</>}. Estos cambios solo afectan a este tour.
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Habitación</Label>
                      <Select value={r.room_type} onValueChange={(v) => setR({ room_type: v, ...(v !== "single" ? { single_supplement: 0 } : {}) })}>
                        <SelectTrigger data-testid={`ep-room-${b.participant_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.keys(ROOM_TYPE).map((rt) => <SelectItem key={rt} value={rt}>{ROOM_TYPE[rt]}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Precio pactado</Label><Input type="number" data-testid={`ep-price-${b.participant_id}`} value={r.price ?? 0} onChange={(e) => setR({ price: e.target.value })} /></div>
                    {r.room_type === "single" && (
                      <div className="col-span-2"><Label>Suplemento habitación simple</Label>
                        <Input type="number" placeholder="Extra que paga por dormir solo" value={r.single_supplement || 0} onChange={(e) => setR({ single_supplement: e.target.value })} />
                        <p className="text-[10px] text-slate-500 mt-1">Se suma al precio pactado al calcular ingresos y saldo.</p>
                      </div>
                    )}
                    <div className="col-span-2"><Label>Pareja (comparte doble, ignora género)</Label>
                      <Select
                        value={r.partner_email || "__none__"}
                        onValueChange={(v) => setR({ partner_email: v === "__none__" ? "" : v })}
                      >
                        <SelectTrigger data-testid={`ep-partner-${b.participant_id}`}><SelectValue placeholder="Sin pareja" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sin pareja —</SelectItem>
                          {b.tour_id === id
                            ? (participants || []).filter((op) => op.id !== b.participant_id && op.email).map((op) => (
                                <SelectItem key={op.id} value={op.email}>{op.first_name} {op.last_name} ({op.email})</SelectItem>
                              ))
                            : <SelectItem value="__other_tour__" disabled>Sólo se puede elegir pareja desde la vista de ese tour</SelectItem>}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-slate-500 mt-1">Solo hace falta configurarlo en uno de los dos participantes.</p>
                    </div>
                    <div className="col-span-2"><Label>Estado</Label>
                      <Select value={r.booking_status} onValueChange={(v) => setR({ booking_status: v })}>
                        <SelectTrigger data-testid={`ep-status-${b.participant_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{["lead","interested","deposit_pending","deposit_paid","confirmed","partial_paid","fully_paid","cancelled","refunded","waitlist"].map((s) => <SelectItem key={s} value={s}>{BOOKING[s]}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPartOpen(false)}>Cancelar</Button>
            <Button data-testid="ep-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={saveEditPart}>
              {activePartTab === "__personal__" ? "Guardar datos personales" : "Guardar cambios de reserva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove participant dialog — transfer to another tour or delete */}
      <Dialog open={removePartOpen} onOpenChange={setRemovePartOpen}>
        <DialogContent className="max-w-lg" data-testid="remove-part-dialog">
          <DialogHeader>
            <DialogTitle>Quitar del tour</DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              {removePartTarget && (<>Elige qué hacer con <b>{removePartTarget.first_name} {removePartTarget.last_name}</b> y sus pagos.</>)}
            </p>
          </DialogHeader>
          <div className="space-y-3">
            <label
              className={`block p-3 rounded-lg border-2 cursor-pointer transition-all ${
                removeMode === "transfer" ? "border-[#026FCB] bg-[#026FCB]/5" : "border-slate-200 dark:border-[#25334D] hover:border-slate-400"
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="remove-mode"
                  value="transfer"
                  checked={removeMode === "transfer"}
                  onChange={() => setRemoveMode("transfer")}
                  className="mt-1 accent-[#026FCB]"
                  data-testid="rm-mode-transfer"
                />
                <div className="flex-1">
                  <div className="font-semibold text-sm">Mover a otro tour</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    El participante conserva sus datos y pagos; simplemente cambia de expedición.
                  </div>
                </div>
              </div>
              {removeMode === "transfer" && (
                <div className="mt-3 pl-6 space-y-2">
                  <Label className="text-xs">Tour destino</Label>
                  <Select value={transferTourId} onValueChange={setTransferTourId}>
                    <SelectTrigger data-testid="rm-target-tour"><SelectValue placeholder="Selecciona un tour…" /></SelectTrigger>
                    <SelectContent>
                      {(allTours || []).filter((t) => t.id !== id).map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                          <span className="text-xs text-slate-400 ml-2">
                            {t.start_date ? fmtDate(t.start_date) : ""}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={movePayments}
                      onChange={(e) => setMovePayments(e.target.checked)}
                      className="accent-[#026FCB]"
                      data-testid="rm-move-pays"
                    />
                    También reasignar sus pagos al nuevo tour
                  </label>
                </div>
              )}
            </label>

            <label
              className={`block p-3 rounded-lg border-2 cursor-pointer transition-all ${
                removeMode === "delete" ? "border-red-500 bg-red-50 dark:bg-red-950/20" : "border-slate-200 dark:border-[#25334D] hover:border-slate-400"
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="remove-mode"
                  value="delete"
                  checked={removeMode === "delete"}
                  onChange={() => setRemoveMode("delete")}
                  className="mt-1 accent-red-500"
                  data-testid="rm-mode-delete"
                />
                <div className="flex-1">
                  <div className="font-semibold text-sm text-red-700 dark:text-red-400">Eliminar del tour</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    El participante deja de estar en esta expedición. Sus pagos quedan registrados en histórico.
                  </div>
                </div>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemovePartOpen(false)}>Cancelar</Button>
            <Button
              data-testid="rm-confirm"
              className={removeMode === "delete" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#026FCB] hover:bg-[#0358a5]"}
              onClick={confirmRemovePart}
              disabled={removeMode === "transfer" && !transferTourId}
            >
              {removeMode === "delete" ? "Eliminar" : "Mover al tour"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment dialog (per participant) */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="add-pay-dialog">
          <DialogHeader>
            <DialogTitle>
              Añadir pago
              {payParticipant && (
                <span className="ml-2 text-sm font-normal text-slate-500">
                  · {payParticipant.first_name} {payParticipant.last_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tipo de pago</Label>
              <Select value={payForm.kind} onValueChange={(v) => setPayForm({ ...payForm, kind: v })}>
                <SelectTrigger data-testid="pay-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["deposit", "second", "final", "extra", "refund"].map((k) => (
                    <SelectItem key={k} value={k}>{PAY_KIND[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Método</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["stripe", "transfer", "cash", "card", "paypal", "other"].map((m) => (
                    <SelectItem key={m} value={m}>{PAY_METHOD[m]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Importe</Label>
              <Input
                type="number"
                data-testid="pay-amount"
                value={payForm.amount}
                onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
              />
            </div>
            <div>
              <Label>Moneda</Label>
              <Select value={payForm.currency} onValueChange={(v) => setPayForm({ ...payForm, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["USD", "EUR", "GBP", "MXN", "CLP", "ARS"].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de cambio (a USD)</Label>
              <Input
                type="number"
                step="0.0001"
                value={payForm.exchange_rate}
                onChange={(e) => setPayForm({ ...payForm, exchange_rate: e.target.value })}
              />
            </div>
            <div>
              <Label>Fecha</Label>
              <DateInputES
                value={payForm.date}
                onChange={(e) => setPayForm({ ...payForm, date: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Referencia</Label>
              <Input
                placeholder="Nº transferencia, recibo…"
                value={payForm.reference}
                onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Notas</Label>
              <Input
                placeholder="Opcional"
                value={payForm.notes || ""}
                onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancelar</Button>
            <Button
              data-testid="pay-save"
              className="bg-[#026FCB] hover:bg-[#0358a5]"
              onClick={savePayment}
            >
              Registrar pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-diem (dietas guías) dialog */}
      <Dialog open={pdOpen} onOpenChange={setPdOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="per-diem-dialog">
          <DialogHeader>
            <DialogTitle>Dietas de guías</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Tour de <b>{tourDays}</b> día(s). Se creará un gasto de categoría <b>Dietas</b> por cada guía seleccionado.
            </p>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 text-[10px] mono uppercase tracking-wider text-slate-500 px-2">
              <div className="col-span-5">Guía</div>
              <div className="col-span-2 text-center">Días</div>
              <div className="col-span-2 text-center">Tarifa/día</div>
              <div className="col-span-2 text-center">Moneda</div>
              <div className="col-span-1 text-right">Total</div>
            </div>
            {pdRows.length === 0 && (
              <div className="text-sm text-slate-500 p-4 text-center border border-dashed border-slate-300 dark:border-[#25334D] rounded">
                Este tour no tiene guías asignados.
              </div>
            )}
            {pdRows.map((r, i) => (
              <div
                key={r.guide_id}
                data-testid={`pd-row-${r.guide_id}`}
                className={`grid grid-cols-12 gap-2 items-center px-2 py-2 rounded transition-colors ${
                  r.selected ? "bg-emerald-50 dark:bg-emerald-950/20" : "bg-slate-50 dark:bg-[#1A2336]/40 opacity-60"
                }`}
              >
                <label className="col-span-5 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-emerald-600"
                    checked={r.selected}
                    onChange={(e) => setPdRows(pdRows.map((row, idx) => idx === i ? { ...row, selected: e.target.checked } : row))}
                  />
                  <span className="text-sm font-medium truncate">{r.guide_name}</span>
                </label>
                <Input
                  type="number"
                  className="col-span-2 h-8 text-center mono"
                  value={r.days}
                  onChange={(e) => setPdRows(pdRows.map((row, idx) => idx === i ? { ...row, days: e.target.value } : row))}
                  data-testid={`pd-days-${r.guide_id}`}
                />
                <Input
                  type="number"
                  className="col-span-2 h-8 text-center mono"
                  value={r.rate}
                  onChange={(e) => setPdRows(pdRows.map((row, idx) => idx === i ? { ...row, rate: e.target.value } : row))}
                  data-testid={`pd-rate-${r.guide_id}`}
                />
                <Select
                  value={r.currency}
                  onValueChange={(v) => setPdRows(pdRows.map((row, idx) => idx === i ? { ...row, currency: v } : row))}
                >
                  <SelectTrigger className="col-span-2 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["USD","EUR","GBP","MXN","CLP","ARS"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="col-span-1 text-right mono font-semibold text-sm">
                  {money((Number(r.rate) || 0) * (Number(r.days) || 0), r.currency)}
                </div>
              </div>
            ))}
            {pdRows.length > 0 && (
              <div className="flex items-center justify-between border-t border-slate-200 dark:border-[#25334D] pt-3 mt-3">
                <span className="text-xs mono uppercase tracking-wider text-slate-500">
                  {pdRows.filter((r) => r.selected).length} seleccionado(s)
                </span>
                <span className="mono font-bold text-lg text-emerald-600">
                  Total: {money(pdRows.filter((r) => r.selected).reduce((s, r) => s + (Number(r.rate) || 0) * (Number(r.days) || 0), 0), pdRows[0]?.currency || "USD")}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdOpen(false)}>Cancelar</Button>
            <Button
              data-testid="pd-save"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={savePerDiem}
              disabled={pdRows.filter((r) => r.selected).length === 0}
            >
              Añadir a gastos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit expense dialog */}
      <Dialog open={expEditOpen} onOpenChange={setExpEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-exp-dialog">
          <DialogHeader><DialogTitle>Editar gasto</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Concepto</Label><Input data-testid="ee-concept" value={expEditForm.concept || ""} onChange={(e) => setExpEditForm({ ...expEditForm, concept: e.target.value })} /></div>
            <div>
              <Label>Categoría</Label>
              <Select value={expEditForm.category} onValueChange={(v) => setExpEditForm({ ...expEditForm, category: v })}>
                <SelectTrigger data-testid="ee-category"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(EXP_CATEGORY).map((k) => <SelectItem key={k} value={k}>{EXP_CATEGORY[k]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de coste</Label>
              <Select value={expEditForm.cost_type || "global"} onValueChange={(v) => setExpEditForm({ ...expEditForm, cost_type: v })}>
                <SelectTrigger data-testid="ee-cost-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global (fijo del tour)</SelectItem>
                  <SelectItem value="per_person">Por persona (escala con pax)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Precio unitario (USD)</Label><Input type="number" step="0.01" data-testid="ee-unit" value={expEditForm.unit_amount ?? ""} onChange={(e) => setExpEditForm({ ...expEditForm, unit_amount: e.target.value })} /></div>
            <div><Label>Cantidad</Label><Input type="number" step="1" min="1" data-testid="ee-qty" value={expEditForm.quantity ?? 1} onChange={(e) => setExpEditForm({ ...expEditForm, quantity: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Total (USD)</Label>
              <div data-testid="ee-total" className="mt-1 px-3 py-2 rounded border border-slate-200 dark:border-[#25334D] bg-slate-50 dark:bg-[#0F1A2E] mono font-semibold text-[#026FCB]">
                {(() => {
                  const u = Number(expEditForm.unit_amount);
                  const q = Number(expEditForm.quantity) || 1;
                  if (Number.isFinite(u) && u > 0) return `US$ ${(u * q).toFixed(2)}  ·  ${u.toFixed(2)} × ${q}`;
                  return `US$ ${(Number(expEditForm.amount) || 0).toFixed(2)}  ·  (sin desglose)`;
                })()}
              </div>
            </div>
            <div><Label>Fecha</Label><DateInputES value={expEditForm.date || ""} onChange={(e) => setExpEditForm({ ...expEditForm, date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notas</Label><Input value={expEditForm.notes || ""} onChange={(e) => setExpEditForm({ ...expEditForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpEditOpen(false)}>Cancelar</Button>
            <Button data-testid="ee-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={saveEditExp}>Guardar cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accommodation (hotel) dialog — crear/editar desde el tour */}
      <Dialog open={accOpen} onOpenChange={(o) => { setAccOpen(o); if (!o) { setAccEditId(null); setAccForm(emptyAcc); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tour-acc-dialog">
          <DialogHeader>
            <DialogTitle>{accEditId ? "Editar hotel" : "Nuevo hotel"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Hotel</Label>
              <Input data-testid="tour-acc-name" value={accForm.hotel_name} onChange={(e) => setAccForm({ ...accForm, hotel_name: e.target.value })} />
            </div>
            <div>
              <Label>Ciudad</Label>
              <Input value={accForm.city} onChange={(e) => setAccForm({ ...accForm, city: e.target.value })} />
            </div>
            <div>
              <Label>Fecha</Label>
              <DateInputES value={accForm.date} onChange={(e) => setAccForm({ ...accForm, date: e.target.value })} />
            </div>
            <div>
              <Label>Check-in</Label>
              <DateInputES value={accForm.check_in} onChange={(e) => setAccForm({ ...accForm, check_in: e.target.value })} />
            </div>
            <div>
              <Label>Check-out</Label>
              <DateInputES value={accForm.check_out} onChange={(e) => setAccForm({ ...accForm, check_out: e.target.value })} />
            </div>
            <div>
              <Label>Noches</Label>
              <Input type="number" value={accForm.nights} onChange={(e) => setAccForm({ ...accForm, nights: e.target.value })} />
            </div>
            <div>
              <Label>Cancelación gratis hasta</Label>
              <DateInputES value={accForm.free_cancellation_until} onChange={(e) => setAccForm({ ...accForm, free_cancellation_until: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Habitaciones (puedes mezclar tipos)</Label>
              <div className="space-y-2 mt-1">
                {(accForm.room_types || []).map((r, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`acc-rt-${idx}`}>
                    <Select value={r.type} onValueChange={(v) => {
                      const next = [...accForm.room_types]; next[idx] = { ...r, type: v };
                      setAccForm({ ...accForm, room_types: next });
                    }}>
                      <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["single","double","twin","triple","quad","quintuple","suite","apartment"].map((t) => (
                          <SelectItem key={t} value={t}>{ROOM_TYPE[t] || t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number" min="0" className="w-20" placeholder="Nº"
                      value={r.count}
                      data-testid={`acc-rt-count-${idx}`}
                      onChange={(e) => {
                        const next = [...accForm.room_types]; next[idx] = { ...r, count: e.target.value };
                        setAccForm({ ...accForm, room_types: next });
                      }}
                    />
                    <span className="text-xs text-slate-400 mono">×</span>
                    <Input
                      type="number" min="0" step="0.01" className="flex-1" placeholder="Precio / hab / noche"
                      value={r.price}
                      data-testid={`acc-rt-price-${idx}`}
                      onChange={(e) => {
                        const next = [...accForm.room_types]; next[idx] = { ...r, price: e.target.value };
                        setAccForm({ ...accForm, room_types: next });
                      }}
                    />
                    <Button
                      size="sm" variant="ghost"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                      onClick={() => {
                        const next = accForm.room_types.filter((_, i) => i !== idx);
                        setAccForm({ ...accForm, room_types: next.length > 0 ? next : [{ type: "double", count: 1, price: 0 }] });
                      }}
                      title="Quitar tipo"
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm" variant="outline" type="button" data-testid="acc-rt-add"
                  onClick={() => setAccForm({
                    ...accForm,
                    room_types: [...(accForm.room_types || []), { type: "double", count: 1, price: 0 }],
                  })}
                >
                  <Plus size={13} className="mr-1" />Añadir tipo de habitación
                </Button>
              </div>
            </div>
            <div className="col-span-2 rounded-lg bg-slate-50 dark:bg-[#1A2336] px-3 py-2 flex items-center justify-between text-sm">
              <span className="mono uppercase text-xs text-slate-500 tracking-wider">Total estancia</span>
              <span className="mono font-semibold text-[#026FCB]">
                {(() => {
                  const nights = Number(accForm.nights) || 1;
                  const total = (accForm.room_types || []).reduce((s, r) => s + (Number(r.count) || 0) * (Number(r.price) || 0) * nights, 0);
                  const rooms = (accForm.room_types || []).reduce((s, r) => s + (Number(r.count) || 0), 0);
                  return `${money(total, accForm.currency)} · ${rooms} hab. × ${nights} noche${nights !== 1 ? "s" : ""}`;
                })()}
              </span>
            </div>
            <div className="col-span-2">
              <Label>Régimen (incluido en el precio)</Label>
              <div className="flex items-center gap-4 mt-2 flex-wrap">
                {[
                  { key: "breakfast_included", label: "Desayuno" },
                  { key: "lunch_included", label: "Comida" },
                  { key: "dinner_included", label: "Cena" },
                ].map((m) => (
                  <label key={m.key} className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-[#026FCB] focus:ring-[#026FCB]"
                      checked={!!accForm[m.key]}
                      data-testid={`acc-meal-${m.key}`}
                      onChange={(e) => setAccForm({ ...accForm, [m.key]: e.target.checked })}
                    />
                    <span className="text-sm">{m.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <Label>Estado</Label>
              <Select value={accForm.confirmation_status} onValueChange={(v) => setAccForm({ ...accForm, confirmation_status: v })}>
                <SelectTrigger data-testid="tour-acc-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["draft", "in_progress", "confirmed", "cancelled"].map((s) => (
                    <SelectItem key={s} value={s}>{ACC_STATUS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Notas</Label>
              <Textarea
                data-testid="tour-acc-notes"
                value={accForm.notes || ""}
                onChange={(e) => setAccForm({ ...accForm, notes: e.target.value })}
                placeholder="Notas internas sobre el hotel: preferencias del contacto, condiciones, incidencias, referencias del pago…"
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccOpen(false)}>Cancelar</Button>
            <Button data-testid="tour-acc-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={saveAcc}>
              {accEditId ? "Guardar cambios" : "Añadir hotel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Itinerary Day Card ---
function ItineraryDayCard({ day, accommodations, onSaved, onDeleted }) {
  const [local, setLocal] = React.useState({
    title: day.title || "",
    accommodation_id: day.accommodation_id || "",
    departure_time: day.departure_time || "",
    description: day.description || "",
  });

  React.useEffect(() => {
    setLocal({
      title: day.title || "",
      accommodation_id: day.accommodation_id || "",
      departure_time: day.departure_time || "",
      description: day.description || "",
    });
  }, [day.id, day.title, day.accommodation_id, day.departure_time, day.description]);

  const persist = async (patch) => {
    try {
      await api.patch(`/itinerary/${day.id}`, patch);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const onBlurTitle = () => {
    if ((local.title || "") !== (day.title || "")) persist({ title: local.title || null });
  };
  const onBlurTime = () => {
    if ((local.departure_time || "") !== (day.departure_time || "")) persist({ departure_time: local.departure_time || null });
  };
  const onBlurDesc = () => {
    if ((local.description || "") !== (day.description || "")) persist({ description: local.description || null });
  };
  const onChangeHotel = (v) => {
    const val = v === "__none__" ? "" : v;
    setLocal((s) => ({ ...s, accommodation_id: val }));
    persist({ accommodation_id: val || null });
  };

  const selectedHotel = (accommodations || []).find((a) => a.id === (local.accommodation_id || day.accommodation_id));

  const handleDelete = async () => {
    const label = day.title || `Día ${day.day_number}`;
    if (!window.confirm(`¿Eliminar "${label}" del itinerario? Los demás días se renumerarán.`)) return;
    try {
      await api.delete(`/itinerary/day/${day.id}`);
      toast.success("Día eliminado");
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar día");
    }
  };

  return (
    <Card data-testid={`itinerary-day-card-${day.day_number}`}>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-lg bg-[#026FCB] text-white flex flex-col items-center justify-center font-bold shrink-0">
            <div className="text-[10px] uppercase tracking-wide opacity-80">Día</div>
            <div className="text-lg leading-none">{day.day_number}</div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-slate-500 uppercase tracking-wide font-medium">{fmtDate(day.date)}</div>
              <Button
                size="sm"
                variant="outline"
                data-testid={`delete-itinerary-day-${day.day_number}`}
                onClick={handleDelete}
                title="Eliminar día"
                className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30 shrink-0"
              >
                <Trash2 size={13} />
              </Button>
            </div>
            <Input
              data-testid={`itinerary-title-${day.day_number}`}
              value={local.title}
              onChange={(e) => setLocal((s) => ({ ...s, title: e.target.value }))}
              onBlur={onBlurTitle}
              placeholder="Título del lugar visitado"
              className="mt-1 text-xl font-semibold border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#026FCB]"
            />
            <div className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              {selectedHotel ? (
                <span><span className="text-slate-400">Hotel:</span> <span className="font-medium">{selectedHotel.hotel_name}</span>{selectedHotel.city ? ` · ${selectedHotel.city}` : ""}</span>
              ) : (
                <span className="text-slate-400 italic">Sin hotel asignado</span>
              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wide">Hotel</Label>
            <Select value={local.accommodation_id || "__none__"} onValueChange={onChangeHotel}>
              <SelectTrigger data-testid={`itinerary-hotel-${day.day_number}`} className="mt-1">
                <SelectValue placeholder="Selecciona un hotel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Sin hotel —</SelectItem>
                {(accommodations || []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.hotel_name}{a.city ? ` · ${a.city}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wide">Hora de salida</Label>
            <Input
              type="time"
              data-testid={`itinerary-time-${day.day_number}`}
              value={local.departure_time}
              onChange={(e) => setLocal((s) => ({ ...s, departure_time: e.target.value }))}
              onBlur={onBlurTime}
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs text-slate-500 uppercase tracking-wide">Descripción del día</Label>
          <Textarea
            data-testid={`itinerary-description-${day.day_number}`}
            value={local.description}
            onChange={(e) => setLocal((s) => ({ ...s, description: e.target.value }))}
            onBlur={onBlurDesc}
            placeholder="Describe qué se hace ese día: rutas, actividades, sesiones fotográficas, horarios..."
            className="mt-1 min-h-[110px]"
          />
        </div>
      </CardContent>
    </Card>
  );
}

